import React, { useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import { useAuth } from '../../Contexts/AuthContext'
import useAdminUserNames from '../../services/hooks/useAdminUserNames'
import { statusLabel, statusTone, isSettled, needsAPerson, wasStopped, requesterName } from '../IngestRequests/requestContract'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import successToast from '../../Components/Toasts/SuccessToast'
import errorToast from '../../Components/Toasts/ErrorToast'
import ConfirmDialog from './ConfirmDialog'
import CompletionSummary, {
  artReason,
  videoReason,
  requestVideoReason,
  requestArtReason,
  lyricsWarningReason,
  itemNameOf,
} from './CompletionSummary'
import TrendingSummary from './TrendingSummary'
import mockRequest from './__mock__/load'
import classes from './index.module.css'
import detail from './detail.module.css'

/**
 * One artist synchronization: where it got to, what it found, and the
 * decisions it cannot make on its own.
 *
 * GET /ingest/requests/:id returns { request, items, events, writes,
 * current_generation } plus live runtime while the request is current.
 */

const POLL_MS = 4000

/**
 * Where an item came from on YouTube. The source id differs by type: a
 * channel for an artist, an OLAK5uy_ playlist for a release, an 11-char
 * video id for a song (catalogue.js).
 */
function youtubeHref(item) {
  if (!item.source_id) return null
  if (item.item_type === 'artist') return `https://music.youtube.com/channel/${item.source_id}`
  if (item.item_type === 'release') return `https://music.youtube.com/browse/${item.source_id}`
  return `https://music.youtube.com/watch?v=${item.source_id}`
}

/**
 * Where it lives in FAST, once matched.
 *
 * ⚠ Usually no artist link: /artist/:artist_name resolves by the artist
 * HANDLE (music_artist.js getArtistByArtistName), and the ingest API sends
 * us only an f_id - a link built from either the f_id or the display name
 * 404s. `artistSlugs`/`writes` let resolveArtistSlug resolve it when it can;
 * every other artist falls through to `return null` below, same as always.
 */
function catalogueHref(item, artistSlugs, writes) {
  if (!item.f_id) return null
  if (item.item_type === 'song') return `/song/${item.f_id}`
  if (item.item_type === 'release') return `/songs/album/${item.f_id}`
  if (item.item_type === 'artist') {
    const slug = resolveArtistSlug(item.f_id, artistSlugs, writes)
    return slug ? `/artist/${slug}` : null
  }
  return null
}

const MATCH_LABELS = {
  source_identity: 'Known source identity',
  legacy_source_identity: 'Existing YouTube identity',
  name_duration: 'Title, artist and duration',
  human_match: 'Confirmed match',
  human_create: 'Confirmed separate',
  new_source: 'New entry',
  song_pairing_3a: 'Matched by YouTube track ID',
  song_pairing_3b: 'Matched by release page and credited artists',
  song_pairing_3c: 'Matched by title, artists and length',
  song_pairing_new: 'New song, created from this track',
  song_pairing_human_match: 'Match you confirmed',
  same_recording_in_request: 'Same recording as another track here',
}

/** A song carries `title`; a release and an artist carry `name`. */
export function stagedName(item) {
  const staged = item && item.staged_data
  if (!staged) return null
  return staged.title || staged.name || null
}

export function stagedExplicit(item) {
  const staged = item && item.staged_data
  if (!staged) return false
  return Boolean(staged.isExplicit || staged.explicit)
}

/** An unrecognised method shows as itself - never as "not matched". */
export function matchLabel(item) {
  if (item.review) return 'Needs a decision'
  if (!item.match_method) return 'Not matched yet'
  return MATCH_LABELS[item.match_method] || item.match_method
}

/**
 * prod_backfill items (ingest/src/store.js stageVideo) are internal backfill
 * evidence, never an incoming track: main's Ingest.jsx (`observedItems`)
 * excludes them from both the count and the rows of its Observed catalogue
 * table. Mirrored here so "Releases and songs (N)" means the same thing.
 */
export function filterCatalogueItems(items) {
  return (items || []).filter(item => !(item.item_type === 'song' && item.staged_data?.prod_backfill === true))
}

/**
 * The Releases-and-songs table's own Matching cell - main's exact three-way
 * wording (Ingest.jsx: `item.review ? 'Needs decision' : matchLabels[...] ||
 * match_method || 'Staged'`). Deliberately not matchLabel() verbatim: that
 * function says "Needs a decision" / "Not matched yet" for the review cards
 * elsewhere on this page, which read fine as sentences; this table's cells
 * read better as main's shorter labels.
 */
export function catalogueMatchLabel(item) {
  if (item.review) return 'Needs decision'
  if (!item.match_method) return 'Staged'
  return matchLabel(item)
}

/**
 * The service writes bookkeeping, not a story: three rows for the intake
 * handshake, then one per entity fetched. This is every kind it can write
 * (ingest/src/store.js). Anything not listed falls through and shows the
 * server's own message, so a new event is never silently swallowed.
 */
/**
 * The server's own message is the default. Override only where every possible
 * message for that kind is jargon. Never add `completed`: several events share
 * it, and a fixed title there turns a failure into "Done".
 */
const EVENT_TITLES = Object.freeze({
  requested: 'Sync requested',
  submitted: 'Sync requested',
  started: 'Sync requested',
  waiting: 'Waiting for a free machine',
})

const EVENT_KEY_TITLES = Object.freeze({
  'dispatch-delayed': 'Waiting for a free machine',
  'workflow-ended': 'Stopped before it finished',
})

// The three intake rows land within a second of each other and mean one thing.
const INTAKE = new Set(['requested', 'submitted', 'started'])

// Temporal's own activity names, in words. Anything unlisted falls back to
// the raw name rather than disappearing.
const ACTIVITY_NAMES = Object.freeze({
  fetchNode: 'Fetching from YouTube',
  commitMetadata: 'Saving to the catalogue',
  reportProgress: 'Reporting progress',
})

export function summarise(events) {
  const out = []
  for (const event of events) {
    const last = out[out.length - 1]

    if (INTAKE.has(event.kind)) {
      // Keep the FIRST timestamp - that is when it was asked for.
      if (last && last.kind === 'intake') continue
      out.push({ id: event.id, kind: 'intake', message: EVENT_TITLES.requested, at: event.date_created })
      continue
    }

    if (event.kind === 'fetched') {
      const type = (event.details && event.details.type) || 'item'
      if (last && last.kind === 'fetched' && last.type === type) {
        last.count += 1
        last.at = event.date_created
        last.message = `Fetched ${last.count} ${type}${last.count === 1 ? '' : 's'}`
        continue
      }
      out.push({ id: event.id, kind: 'fetched', type, count: 1, message: `Fetched 1 ${type}`, at: event.date_created })
      continue
    }

    const message =
      EVENT_KEY_TITLES[event.event_key] || EVENT_TITLES[event.kind] || event.message || ''
    // Consecutive steps that say the same thing are one step.
    if (last && last.message === message) {
      last.at = event.date_created
      continue
    }
    out.push({ id: event.id, kind: event.kind, message, at: event.date_created })
  }
  return out
}

function when(value) {
  if (!value) return '—'
  const d = new Date(value)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

/**
 * Before the backend PR below shipped, the ONLY place an artist's FAST slug
 * (mmuf_artist.artist_name) was ever reachable from this API: a brand-new
 * artist's own insert write (content-admin/ingest/src/catalogue.js:2830-2831,
 * createNoChannelArtist's `auditedWrite(db, request, 'mmuf_artist', {f_id},
 * {name, artist_name, is_active, is_deleted})` - the only `mmuf_artist`
 * INSERT in the whole file). Still the fallback for a response from before
 * that PR lands everywhere - see resolveArtistSlug.
 */
function artistSlugFromWrites(writes, fId) {
  if (!fId) return null
  const write = (writes || []).find(w => w.table_name === 'mmuf_artist' && w.after_data && w.after_data.f_id === fId)
  return (write && write.after_data.artist_name) || null
}

/**
 * An artist's FAST slug, in priority order:
 * 1. `data.artist_slugs[f_id]` - a top-level `{[artist f_id]: artist_name}`
 *    map a backend PR is adding (API-PAYLOAD.md), covering the request's own
 *    artist, every artist item, and every artist review candidate. Pending
 *    merge - absent from every response until it ships.
 * 2. `artistSlugFromWrites` - the one case reachable without it (an artist
 *    THIS request just created).
 * 3. null - every caller's plain-text fallback (HANDOFF.md section 3: no
 *    existing admin-api endpoint resolves an f_id to a slug any other way -
 *    confirmed directly against every route in admin-service-api/route_1.0/
 *    music_artist.js, which are all keyed by the slug already, never by
 *    f_id).
 */
export function resolveArtistSlug(fId, artistSlugs, writes) {
  if (!fId) return null
  if (artistSlugs && artistSlugs[fId]) return artistSlugs[fId]
  return artistSlugFromWrites(writes, fId)
}

/** Ingest.jsx's own `value()`: a write's row_pk/before_data/after_data field
 * values are whatever Postgres gave back (RETURNING *), so an object (jsonb,
 * or row_pk itself) prints as its raw JSON - the same thing main's page
 * already shows, never a prettied-up "key: value" paraphrase. */
function auditValue(data) {
  return data == null ? '—' : typeof data === 'object' ? JSON.stringify(data) : String(data)
}

function seconds(value) {
  if (typeof value !== 'number' || !isFinite(value) || value <= 0) return null
  // Round ONCE, then divide - rounding the remainder separately renders 1:60.
  const total = Math.round(value)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** The incoming listing uses `duration_seconds`, a candidate `length_seconds`. */
function lengthOf(row) {
  if (!row) return null
  return typeof row.duration_seconds === 'number' ? row.duration_seconds : row.length_seconds
}

export function lengths(row) {
  const all = [lengthOf(row), ...(row.videos || []).map(lengthOf)]
  const found = [...new Set(all.map(seconds).filter(Boolean))]
  return found.join(' / ') || null
}

/** What a person already chose for an item, in their words. */
export function decisionSummary(item) {
  const decision = item && item.decision
  if (!decision) return null
  // A video review's own answer lives nested at decision.video (store.js
  // action(), never a plain decision.action of 'video_use'/'video_keep') -
  // see isReviewAnswered's own comment on why. Checked by review.kind, not
  // decision shape, since an unrelated earlier decision can still be sitting
  // on `decision` itself (same reason isReviewAnswered exists at all).
  if (item.review && item.review.kind === 'video') {
    const video = decision.video
    if (!video) return null
    return video.action === 'video_use'
      ? `you chose to use this video (intro gap ${video.gap_ms ?? 0} ms)`
      : 'you chose to keep the art track'
  }
  if (decision.action === 'create') return 'you said this one is new'
  const candidates = (item.review && item.review.candidates) || []
  const target = candidates.find(c =>
    decision.target_item_id != null
      ? String(c.item_id) === String(decision.target_item_id)
      : c.f_id === decision.f_id
  )
  const name = target && (target.title || target.name)
  return name ? `you matched it to “${name}”` : 'you matched it to an existing entry'
}

/** The incoming listing names it `title`; a catalogue candidate uses `name`. */
export function rowName(row) {
  return (row && (row.title || row.name)) || null
}

// ---------------------------------------------------------------------------
// Decision cards (item-type badge, whole-card option selection, side-by-side
// incoming/candidate comparison). One ReviewCard per open review; it
// dispatches to a kind-specific card below so every review shape only ever
// reads the fields that kind's own payload actually carries (API-PAYLOAD.md).
// ---------------------------------------------------------------------------

const ITEM_TYPE_BADGE = Object.freeze({ song: 'Song', release: 'Release', artist: 'Artist' })

/** Song/Release/Artist from item_type, except an official-video review
 * (item_type 'song', review.kind 'video') reads as "Video" - it is not a
 * catalogue match/create decision at all, just which video to use. */
function reviewBadge(item) {
  if (item.review.kind === 'video') return 'Video'
  return ITEM_TYPE_BADGE[item.item_type] || item.item_type
}

/** The header row every decision card shares: an eyebrow "From YouTube" (the
 * card's own title IS the YouTube item - owner correction, 2026-10-08) with
 * the item-type pill at its right, then the title itself, then the reason.
 * The data box further down (IncomingBlock) never repeats this label. */
function ReviewHead({ headingId, title, badge, reason }) {
  return (
    <legend id={headingId} className={detail.reviewHead}>
      <span className={detail.reviewTitleRow}>
        <span className={detail.incomingLabel}>From YouTube</span>
        <span className={classes.badge}>{badge}</span>
      </span>
      <span className={detail.reviewName}>{title}</span>
      <span className={detail.reviewWhy}>{reason}</span>
    </legend>
  )
}

ReviewHead.propTypes = { headingId: PropTypes.string.isRequired, title: PropTypes.node.isRequired, badge: PropTypes.string.isRequired, reason: PropTypes.node }

function formatArtists(list) {
  return (list || []).map(a => a && a.name).filter(Boolean).join(', ') || null
}

/**
 * Song-pairing (plan Step 3) reviews carry a different evidence shape than a
 * legacy (pre-song-pairing) review an already-running request can still
 * produce (catalogue.js resolveSong vs the old resolve()'s songEvidence):
 * the new incoming object always has a `video_type` key (even when its value
 * is null); the old one never does. Guarding on the KEY, not its value, so
 * an old-shape review this page must still render correctly is never
 * mistaken for the richer new one. Ported from Ingest.jsx unchanged.
 */
function isNewShapeSongReview(item) {
  return item.item_type === 'song' && !!item.review.incoming && Object.hasOwn(item.review.incoming, 'video_type')
}

/**
 * Legacy evidence rows spell a video id `source_id` (on the row itself and
 * on each item of `row.videos[]` - Ingest.jsx's own `sources()`). The new
 * song-pairing shape spells the same concept `video_source_id` instead
 * (catalogue.js videoEvidence) - two different field names for the same
 * idea, read by two different helpers, so neither path ever silently reads
 * the other's spelling and renders nothing.
 */
function legacyVideoId(row) {
  if (!row) return null
  const own = typeof row.source_id === 'string' && /^[\w-]{11}$/.test(row.source_id) ? row.source_id : null
  if (own) return own
  const fromVideos = (row.videos || []).find(v => typeof v.source_id === 'string' && /^[\w-]{11}$/.test(v.source_id))
  return (fromVideos && fromVideos.source_id) || null
}

/** A video review's choice is a plain string ('video_keep' or
 * 'video_use:<gap_ms>'), unlike every other kind's {action,key,...} object -
 * an empty or non-numeric gap is not a real choice yet (owner review on
 * Ingest.jsx: "an empty value stays empty", never silently coerced to 0). */
function videoChoiceValid(choice) {
  return choice === 'video_keep' ||
    (typeof choice === 'string' && choice.startsWith('video_use:') &&
      choice.slice('video_use:'.length) !== '' && Number.isFinite(Number(choice.slice('video_use:'.length))))
}

/** Has a choice been made IN THIS BROWSER SESSION (the `choices` state
 * object), not yet sent? See isReviewAnswered below for whether the SERVER
 * already has a real answer on file - the two are not the same question. */
function isReviewDecided(item, choice) {
  if (!choice) return false
  return item.review.kind === 'video' ? videoChoiceValid(choice) : true
}

/**
 * Does `item.decision` actually answer the review `item.review` CURRENTLY
 * carries - not just "does a decision exist"? store.js never clears
 * `decision` when a later pass raises a NEW review on the same item, so an
 * item can carry a stale decision from an earlier round while a brand new,
 * genuinely open review sits right next to it. This is the real bug seen on
 * space-4 request 52a6bcd8 (item 7150, source CDUJr4ujl6w): a song review
 * was answered with `{action:'create'}` in round 1; a later pass raised a
 * `kind:'video'` review on the SAME item, but `item.decision` was still
 * truthy (the round-1 create), so the page treated the video review as
 * already answered and hid it - "needs_review" with nothing to answer.
 *
 * The one kind where a decision can layer on top of an earlier one instead
 * of replacing it is 'video': store.js action() (catalogue.js:887's own
 * `p.item.decision?.video`) writes the video choice NESTED as
 * `decision.video` precisely so it survives alongside whatever decision an
 * earlier round already wrote - `{...existingDecision, video}` - never
 * overwriting it. So a video review is answered only when `decision.video`
 * itself is present; every other kind keeps today's rule (any decision at
 * all answers it, since nothing else layers a second review on one item).
 */
/**
 * True when an operator action (SEND = kind 'review', RECHECK MATCHING =
 * kind 'retry') is newer than the workflow's last needs_review event. The
 * dispatcher (client.js deliver -> store.dispatched) sets delivered_revision
 * the moment it SIGNALS the workflow, but status stays 'needs_review' until
 * the workflow's next progress() write - so revision === delivered_revision
 * alone cannot tell "waiting on a person" from "answered, workflow not moved
 * yet". Without this, the page re-opened every card (choices just cleared)
 * and stopped polling, so it looked like the decisions were never sent
 * until a manual reload. A re-raised review writes a fresh needs_review
 * event (workflows.js progress key carries the cycle), which flips this
 * back to false.
 */
export function actionNotYetResumed(events) {
  let lastAction = -Infinity
  let lastWait = -Infinity
  for (const e of events || []) {
    if (e.kind === 'review' || e.kind === 'retry') lastAction = Math.max(lastAction, e.id)
    else if (e.kind === 'needs_review') lastWait = Math.max(lastWait, e.id)
  }
  return lastAction > lastWait
}

export function isReviewAnswered(item) {
  if (!item.decision) return false
  if (item.review.kind === 'video') return Boolean(item.decision.video)
  return true
}

/**
 * Owner rule (2026-10-09): a saved decision may pre-select a choice on a
 * card only when it still answers the review the item CURRENTLY carries -
 * never because it exists at all (that was isReviewAnswered's old job, and
 * is no longer allowed to hide anything - see openReviews's own comment).
 * This is a presentation hint only: the caller still requires SEND before
 * anything is submitted, same as a choice the operator just clicked.
 *
 * - video: only when decision.video.video_id equals THIS review's own
 *   release_page_id (store.js action() stamps video_id from item.review.
 *   release_page_id at the moment the decision was written, so a changed
 *   release_page_id on a later-raised review means the old choice was about
 *   a different video entirely).
 * - create: only when review.can_create isn't false.
 * - match: only when the matched candidate is still actually listed on
 *   this review - by f_id, or by target_item_id against a staged (within-
 *   request) candidate's own item_id.
 * - release_pick_one is answered as a whole GROUP by onPickOne writing
 *   straight into `choices` (every options[].item_id in one submission,
 *   store.js action()) - never through this per-item hint, which has no way
 *   to pre-select one option without leaving its siblings out of the
 *   eventual payload. Always null here; left exactly as onPickOne already
 *   handles it.
 */
export function decisionFitsReview(item) {
  const decision = item && item.decision
  const review = item && item.review
  if (!decision || !review) return null
  if (review.kind === 'release_pick_one') return null
  if (review.kind === 'video') {
    const video = decision.video
    if (!video || video.video_id !== review.release_page_id) return null
    return video.action === 'video_use' ? `video_use:${video.gap_ms ?? 0}` : 'video_keep'
  }
  if (decision.action === 'create') {
    return review.can_create !== false ? { action: 'create', item_id: item.id } : null
  }
  if (decision.action !== 'match') return null
  const candidates = review.candidates || []
  const candidate = decision.target_item_id != null
    ? candidates.find(c => c.staged && String(c.item_id) === String(decision.target_item_id))
    : candidates.find(c => c.f_id === decision.f_id)
  if (!candidate) return null
  // Mirrors each card's own key format: SongReviewCard is the only one that
  // ever points at a staged, within-request candidate by item_id; release
  // and artist cards key every option by catalogue f_id only.
  const key = item.item_type === 'song' && candidate.staged && candidate.item_id != null
    ? `item:${candidate.item_id}`
    : `entry:${candidate.f_id}`
  return decision.target_item_id != null
    ? { action: 'match', key, item_id: item.id, target_item_id: String(decision.target_item_id) }
    : { action: 'match', key, item_id: item.id, f_id: candidate.f_id }
}

/** release_pick_one's own dedupe key (build spec; same approach as
 * Ingest.jsx's pickOneGroupKey): the catalogue release plus the group's own
 * sorted option ids, since a later pick-one could coincidentally share a
 * prod f_id with an earlier one's loser. */
function pickOneGroupKey(review) {
  return `${review.prod.f_id}:${review.options.map(o => o.item_id).sort().join(',')}`
}

/**
 * The whole-card click target (item 5): a focusable div, role="radio", never
 * a native <label> wrapping a nested <a>/<iframe> - a label's click also
 * fires its control even when the actual click landed on a link inside it,
 * which would make "Open in FAST" also change the selection. Every <a> and
 * interactive child inside an OptionCard must stopPropagation its own click
 * so it only does its own thing; an iframe needs no such handling - a click
 * inside a cross-origin iframe never bubbles to this page at all.
 */
function OptionCard({ selected, disabled, onSelect, ariaLabel, children }) {
  function onKeyDown(event) {
    if (disabled) return
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      onSelect()
    }
  }
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      className={[detail.optionCard, selected && detail.optionCardOn, disabled && detail.optionCardDisabled]
        .filter(Boolean)
        .join(' ')}
      onClick={() => !disabled && onSelect()}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  )
}

OptionCard.propTypes = {
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
}

/**
 * [{label, value, diff?}] rows, dropping any row with no value at all
 * (never a fabricated "—" for a field that simply is not in a shape's
 * payload - e.g. a song-pairing candidate carries no release/album name).
 * `diff` (item 4: "highlight differing fields") marks a value that differs
 * from the one "From YouTube" block shown once at the top of the card.
 */
function FieldList({ rows }) {
  const shown = rows.filter(r => r.value != null)
  if (!shown.length) return null
  return (
    <>
      {shown.map(r => (
        <div key={r.label} className={r.diff ? `${detail.compareRow} ${detail.compareDiff}` : detail.compareRow}>
          <span className={detail.compareField}>{r.label}</span>
          <span className={detail.compareValue}>{r.value}</span>
        </div>
      ))}
    </>
  )
}

FieldList.propTypes = { rows: PropTypes.array.isRequired }

/** Plain fields, no comparison - for the one-time "From YouTube" block. */
function fieldRowsPlain(pairs) {
  return pairs.map(([label, value]) => ({ label, value: value == null ? null : value }))
}

/** A possible-duplicate's own fields, each flagged when it differs from the
 * value `incomingValue` the "From YouTube" block already showed once. */
function fieldRows(triples) {
  return triples.map(([label, value, incomingValue]) => ({
    label,
    value: value == null ? null : value,
    diff: value != null && incomingValue != null && String(value) !== String(incomingValue),
  }))
}

/**
 * One external YouTube id, named by which field it came from (never guessed
 * from the id's own shape when the payload already says what it is) and
 * linked to the real page that id opens - owner correction, 2026-10-08.
 * Never diffed against anything: a possible-duplicate naturally has its own,
 * different id, which is not a mismatch worth flagging the way a title or
 * duration is.
 */
function idRow(label, id, href) {
  if (!id) return null
  return {
    label,
    value: (
      <a className={detail.idRowLink} href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
        {id}
      </a>
    ),
  }
}

const YOUTUBE_WATCH = id => `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`
const YOUTUBE_CHANNEL = id => `https://music.youtube.com/channel/${encodeURIComponent(id)}`
const YOUTUBE_RELEASE_PAGE = id => `https://music.youtube.com/browse/${encodeURIComponent(id)}`
const YOUTUBE_AUDIO_PLAYLIST = id => `https://music.youtube.com/playlist?list=${encodeURIComponent(id)}`

/**
 * The real reason "Add it — it's new" is blocked, read from the payload
 * rather than a fixed guess per item type (owner correction, 2026-10-08: the
 * artist card's own "merge two artists' catalogues" copy was the OLD
 * artist-merge warning, wrong for the actual reason it was blocked here).
 * catalogue.js's shared resolve() (content-admin/ingest/src/catalogue.js:1429
 * for an artist, :3695 for every type) sets `can_create: exact.length === 0`
 * and, whenever `exact.length` is what blocked it, writes the SAME reason
 * text - 'Multiple catalogue entries use this source identity.' - for every
 * item type; `review.candidates` IS that `exact` list in this one case, so
 * its own length is the real, true count. Any other `review.reason` (song
 * reviews in particular can have several non-duplicate-source causes -
 * API-PAYLOAD.md) has no specific translation yet, so this shows the
 * server's own reason verbatim rather than a fixed sentence that would be
 * wrong for it.
 */
function canCreateBlockedReason(review, noun, typeLabel) {
  if (review.can_create) return null
  if (review.reason === 'Multiple catalogue entries use this source identity.') {
    const n = (review.candidates || []).length
    return `Can't add as new: ${n} catalogue ${typeLabel}${n === 1 ? '' : 's'} already use${n === 1 ? 's' : ''} this YouTube ${noun}. Pick the one that really is this ${typeLabel}.`
  }
  return review.reason || `This cannot be added as a new ${typeLabel}.`
}

/**
 * A release candidate's `youtube_ids[]` (catalogue.js candidateReleaseSide)
 * is the one shape here with no named fields to read, only a flat mapped
 * list - the ONE case owner correction 2026-10-08 allows guessing a label
 * from the id's own prefix instead of the payload saying which. An
 * unrecognized prefix still shows the real id, as plain text with no link,
 * never a guessed (and possibly wrong) URL.
 */
function releaseIdRow(id) {
  if (id.startsWith('MPREb_')) return idRow('YouTube release page ID', id, YOUTUBE_RELEASE_PAGE(id))
  if (id.startsWith('OLAK5uy_')) return idRow('YouTube audio playlist ID', id, YOUTUBE_AUDIO_PLAYLIST(id))
  return { label: 'Other YouTube ID', value: id }
}

/** The one id in `ids[]` with the given prefix, or null - used to pull the
 * release-page/audio-playlist id out of a candidate's flat youtube_ids[] by
 * NAME rather than by array position, so that row's position never shifts
 * depending on what order the backend happened to return the ids in. */
function pickReleaseId(ids, prefix) {
  return (ids || []).find(id => typeof id === 'string' && id.startsWith(prefix)) || null
}

/** Whatever is left in `ids[]` once the two named ones are pulled out - a
 * real id the payload sent that this page has no name for yet, never
 * dropped, just not one of the two guaranteed rows. */
function otherReleaseIds(ids) {
  return (ids || []).filter(id => !(id.startsWith('MPREb_') || id.startsWith('OLAK5uy_')))
}

/**
 * Owner correction, 2026-10-08: a release card's rows must always all
 * render, on both the From YouTube side and every possible-duplicate side,
 * so the two line up - "Unknown" (muted) in place of a value this side's
 * payload simply does not carry (e.g. incomingReleaseSide never has
 * Artist(s) at all), never a hidden row. `value` may be 0 - only null/
 * undefined counts as missing.
 */
function releaseFieldRow(label, value, incomingValue) {
  return {
    label,
    value: value == null ? <span className={detail.placeholderValue}>Unknown</span> : value,
    // Only meaningful on a possible-duplicate's own rows, which pass a real
    // `incomingValue` to compare against; the From YouTube side's own rows
    // never pass a third argument, so this is always false there.
    diff: value != null && incomingValue != null && String(value) !== String(incomingValue),
  }
}

/** Same, but for an id: "None" (muted), never "Unknown" - a release
 * genuinely having no YouTube playlist id (say) is a fact about it, not
 * something merely unobserved. Reuses idRow for the "present" case so the
 * link styling/behavior (stopPropagation, new tab) stays identical. */
function releaseIdFieldRow(label, id, href) {
  return idRow(label, id, href) || { label, value: <span className={detail.placeholderValue}>None</span> }
}

/** The possible-duplicate side's own "Catalogue ID" row - "None" (muted)
 * rather than hidden when, somehow, a catalogue candidate has no f_id. Never
 * shown on the From YouTube side: an unmatched YouTube item has no
 * catalogue id to not-have, so there is nothing for it to line up against. */
function catalogueIdFieldRow(fId) {
  if (!fId) return { label: 'Catalogue ID', value: <span className={detail.placeholderValue}>None</span> }
  return {
    label: 'Catalogue ID',
    value: (
      <Link className={detail.idRowLink} to={`/songs/album/${fId}`} target="_blank" onClick={e => e.stopPropagation()}>
        {fId}
      </Link>
    ),
  }
}

/**
 * The ONE YouTube item a review card is about: its fields, shown once near
 * the top, right under ReviewHead's own "From YouTube" eyebrow - never
 * repeated here (owner correction, 2026-10-08: "it would be said twice"),
 * and never relabeled "Candidate" either - every existing catalogue entry
 * below it is the thing competing for that word in ordinary speech, so this
 * page never uses it at all. `videoId` left undefined (not null) skips the
 * video section entirely - a release has no video at all, never "no video
 * on file".
 */
function IncomingBlock({ rows, videoId, videoLabel, channelHref }) {
  const shown = rows.filter(r => r.value != null)
  if (!shown.length && videoId === undefined && !channelHref) return null
  const rowsAndChannel = (
    <>
      {shown.map(r => (
        <div key={r.label} className={detail.compareRow}>
          <span className={detail.compareField}>{r.label}</span>
          <span className={detail.compareValue}>{r.value}</span>
        </div>
      ))}
      {channelHref && (
        <a className={detail.look} href={channelHref} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
          View channel
        </a>
      )}
    </>
  )
  // Only a song's "From YouTube" block ever passes a videoId (a release/
  // artist has no video at all - see videoId's own undefined-skips-it
  // comment below) - so only that one needs the rows-left/player-right
  // split (owner correction, 2026-10-08, item 2); every other call keeps the
  // plain stacked rows it always had.
  if (videoId === undefined) {
    return <div className={detail.incomingBlock}>{rowsAndChannel}</div>
  }
  return (
    <div className={detail.incomingBlock}>
      <div className={detail.incomingSplit}>
        <div className={detail.incomingSplitRows}>{rowsAndChannel}</div>
        <div className={detail.incomingSplitMedia}>
          <YoutubeFrame id={videoId} title={videoLabel || 'From YouTube'} />
        </div>
      </div>
    </div>
  )
}

IncomingBlock.propTypes = { rows: PropTypes.array.isRequired, videoId: PropTypes.string, videoLabel: PropTypes.string, channelHref: PropTypes.string }

/**
 * A small 16:9 YouTube player, or the honest "nothing to show" state - never
 * a placeholder image standing in for a video that was never there. Starts
 * as a lightweight click-to-play preview (the real YouTube thumbnail image,
 * no iframe) and only swaps in the actual youtube-nocookie embed once
 * clicked (owner correction, 2026-10-08: a real request has 68 decision
 * cards - 68 live iframes at once was the "not a user friendly size"
 * problem, not just their width). Clicking the preview plays the video and
 * must NOT also select/deselect the OptionCard it sits inside - same
 * stopPropagation every other in-card link/input on this page already uses.
 */
function YoutubeFrame({ id, title }) {
  const [playing, setPlaying] = useState(false)
  if (!id) return <p className={detail.ytMissing}>No YouTube video on file</p>
  if (!playing) {
    return (
      <button
        type="button"
        className={detail.ytThumbButton}
        aria-label={`Play ${title}`}
        onClick={event => {
          event.stopPropagation()
          setPlaying(true)
        }}
      >
        <img
          className={detail.ytThumbImg}
          src={`https://i.ytimg.com/vi/${encodeURIComponent(id)}/mqdefault.jpg`}
          alt=""
          loading="lazy"
        />
        <span className={detail.ytPlayBadge} aria-hidden="true">
          <span className={detail.ytPlayCircle}>
            <span className={detail.ytPlayTriangle} />
          </span>
        </span>
      </button>
    )
  }
  return (
    <div className={detail.ytFrameWrap}>
      <iframe
        title={title}
        loading="lazy"
        allowFullScreen
        allow="autoplay; encrypted-media; picture-in-picture"
        src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1`}
      />
    </div>
  )
}

YoutubeFrame.propTypes = { id: PropTypes.string, title: PropTypes.string.isRequired }

/** `data.song_albums[candidate.f_id]` (store.js's songAlbumsFor) - the prod
 * album name(s) this candidate is already on, which nothing on the
 * candidate itself carries (API-PAYLOAD.md). `null`/empty renders nothing;
 * one album is labelled "Album", two or more "Albums", each name linking to
 * its own FAST page the same way `candidate.f_id` already does below. */
function albumRow(albums) {
  if (!albums || !albums.length) return null
  return {
    label: albums.length > 1 ? 'Albums' : 'Album',
    value: albums.map((album, i) => (
      <React.Fragment key={album.f_id}>
        {i > 0 && ', '}
        <Link className={detail.idRowLink} to={`/songs/album/${album.f_id}`} target="_blank" onClick={e => e.stopPropagation()}>
          {album.name || album.f_id}
        </Link>
      </React.Fragment>
    )),
  }
}

/** `candidate.release_names` (catalogue.js recordingCandidate) - the YouTube
 * release name(s) a STAGED "other recording in this request" sibling is on.
 * A staged sibling's f_id is provisional (not yet in prod), so song_albums
 * never has an entry for it - this is the staged equivalent of albumRow. */
function releaseNamesRow(names) {
  if (!names || !names.length) return null
  return { label: names.length > 1 ? 'Releases' : 'Release', value: names.join(', ') }
}

/** One possible-duplicate's body, inside its own OptionCard. Handles both
 * the song-pairing (new) and legacy evidence shapes - see
 * isNewShapeSongReview. `incoming` is the same {title, artists, duration,
 * videoType, videoId} values SongReviewCard already showed once in its
 * IncomingBlock, passed down only so a differing value can be highlighted.
 * `albums` is this candidate's own `song_albums` entry (see albumRow). */
function SongCandidateBody({ incoming, candidate, isNew, index, total, albums }) {
  const candidateVideos = isNew ? (candidate.videos && candidate.videos.length ? candidate.videos : []) : []
  const dedupedVideos = candidateVideos.filter(
    (v, i) => v.video_source_id == null || candidateVideos.findIndex(o => o.video_source_id === v.video_source_id) === i
  )
  const candidateVideoId = isNew ? (dedupedVideos[0] && dedupedVideos[0].video_source_id) || candidate.video_source_id || null : legacyVideoId(candidate)

  const rows = (isNew
    ? fieldRows([
        ['Title', candidate.name, incoming.title],
        ['Artists', formatArtists(candidate.artists), incoming.artists],
        ['Duration', seconds(candidate.length_seconds), incoming.duration],
        ['Video type', candidate.music_video_type, incoming.videoType],
      ])
    : fieldRows([
        ['Title', rowName(candidate), incoming.title],
        ['Artists', formatArtists(candidate.artists), incoming.artists],
        ['Duration', lengths(candidate), incoming.duration],
      ])
  ).concat([
      idRow('YouTube video ID', candidateVideoId, candidateVideoId && YOUTUBE_WATCH(candidateVideoId)),
      albumRow(albums),
      candidate.staged ? releaseNamesRow(candidate.release_names) : null,
    ].filter(Boolean))

  return (
    <>
      <div className={detail.optionHead}>
        <span className={detail.optionLabel}>{candidate.name || 'Untitled'}</span>
        <span className={detail.optionSub}>
          {total > 1 ? `Possible duplicate ${index + 1} of ${total}` : 'Possible duplicate in the catalogue'}
          {candidate.staged ? ' — also in this request' : ''}
          {' — pick this if it’s the same; it won’t be added again'}
        </span>
      </div>
      {/* Owner correction, 2026-10-08 (round 2): NEVER a rows-left/player-
          right split inside an option card. A possible-duplicate sits inside
          .compareGrid, which can legitimately be 3+ across (~520px per
          card) - splitting that again would squeeze the rows column down to
          the point where every value wraps one character per line (the
          actual bug reported). Player on top (full width, capped at 320px
          by .ytFrameWrap/.ytThumbButton themselves), rows below, same as
          every other option card (VideoReviewCard's two choices never split
          either). */}
      <YoutubeFrame id={candidateVideoId} title="Possible duplicate's video" />
      <FieldList rows={rows} />
      {!candidate.staged && candidate.f_id && (
        <p className={detail.code}>
          Catalogue ID: {candidate.f_id}{' '}
          <Link className={detail.look} to={`/song/${candidate.f_id}`} target="_blank" onClick={e => e.stopPropagation()}>
            Open in FAST
          </Link>
        </p>
      )}
    </>
  )
}

SongCandidateBody.propTypes = {
  incoming: PropTypes.object.isRequired,
  candidate: PropTypes.object.isRequired,
  isNew: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  albums: PropTypes.array,
}

function SongReviewCard({ item, choice, onChoose, disabled, songAlbums }) {
  const isNew = isNewShapeSongReview(item)
  const review = item.review
  const incomingRaw = review.incoming
  const staged = item.staged_data || {}
  const candidates = review.candidates || []
  const headingId = `review-${item.id}-heading`
  // review.listings (catalogue.js resolveSongGroups) is the only place a
  // release NAME lives on a song-pairing review - neither incoming nor a
  // candidate carries one (reported as a gap: a song-pairing candidate has
  // no release/album field at all, and no track number either). This
  // recording can be a listing on several releases at once (e.g. the
  // original album and a Best Of comp), so every distinct name across
  // review.listings is shown, in the order the backend already sent them -
  // never just the one listing matching this item's own ids. A listing's
  // own `release_names` (array) is deduped directly; `release_name` (a
  // ' / '-joined string, for a card saved before `release_names` existed)
  // is split first - otherwise the SAME name joined into 2 different
  // listings' strings (e.g. "A / B" and "A") would show up twice.
  // Legacy-shape reviews carry their release names on `incoming` itself
  // (catalogue.js songEvidence) - there is no `listings` array there.
  const releaseNames = isNew
    ? [...new Set((review.listings || []).flatMap(l => (l.release_names && l.release_names.length ? l.release_names : (l.release_name ? l.release_name.split(' / ') : []))))]
    : [...new Set((incomingRaw && incomingRaw.release_names || []).filter(Boolean))]
  const incomingVideoId = isNew ? incomingRaw.art_track_id || item.source_id : legacyVideoId(incomingRaw)
  // The normalized values every possible-duplicate below compares itself
  // against, so "From YouTube" is computed once, not re-derived per option.
  const incoming = isNew
    ? {
        title: incomingRaw.title,
        artists: formatArtists(incomingRaw.artists),
        duration: seconds(incomingRaw.duration_seconds),
        videoType: incomingRaw.video_type,
        videoId: incomingVideoId,
      }
    : {
        title: rowName(incomingRaw),
        artists: formatArtists(incomingRaw && incomingRaw.artists),
        duration: lengths(incomingRaw || {}),
        videoId: incomingVideoId,
      }
  // art_track_id and release_page_id are two separately-named payload fields
  // (owner correction, 2026-10-08: label by kind when the payload says
  // which) - shown as two rows only when they are actually two different
  // ids; `same_id` reviews (the common case) carry the same value in both,
  // and a single "YouTube video ID" row says that honestly instead of
  // repeating one id under two labels.
  const incomingVideoIdRows = isNew
    ? incomingRaw.art_track_id && incomingRaw.release_page_id && incomingRaw.art_track_id !== incomingRaw.release_page_id
      ? [
          idRow('Art track video ID', incomingRaw.art_track_id, YOUTUBE_WATCH(incomingRaw.art_track_id)),
          idRow('Release page video ID', incomingRaw.release_page_id, YOUTUBE_WATCH(incomingRaw.release_page_id)),
        ]
      : [idRow('YouTube video ID', incomingVideoId, incomingVideoId && YOUTUBE_WATCH(incomingVideoId))]
    : [idRow('YouTube video ID', incomingVideoId, incomingVideoId && YOUTUBE_WATCH(incomingVideoId))]
  const incomingRows = (isNew
    ? fieldRowsPlain([
        ['Title', incoming.title],
        ['Artists', incoming.artists],
        ['Duration', incoming.duration],
        ['Release/album', releaseNames.length ? releaseNames.join(', ') : null],
        ['Track number', staged.track != null ? String(staged.track) : null],
        ['Explicit', staged.isExplicit === true ? 'Yes' : staged.isExplicit === false ? 'No' : null],
        ['Video type', incoming.videoType],
      ])
    : fieldRowsPlain([
        ['Title', incoming.title],
        ['Artists', incoming.artists],
        ['Duration', incoming.duration],
        ['Release/album', releaseNames.length ? releaseNames.join(', ') : null],
      ])
  ).concat(incomingVideoIdRows.filter(Boolean))
  return (
    <fieldset className={detail.reviewItem} role="radiogroup" aria-labelledby={headingId}>
      <ReviewHead headingId={headingId} title={stagedName(item) || 'Untitled'} badge={reviewBadge(item)} reason={review.reason} />
      <p className={detail.decisionLead}>Is this YouTube item already in the catalogue? Pick the duplicate, or add it as new.</p>
      <IncomingBlock rows={incomingRows} videoId={incomingVideoId} videoLabel="From YouTube" />
      {/* Candidates + "Add it" side by side wherever 2+ fit (owner
          correction, 2026-10-08, item 3) - never a fixed 2-up grid, so a
          single leftover option (no candidates at all) still fills the row
          instead of sitting half-width - see .compareGrid's own comment. */}
      <div className={detail.compareGrid}>
        {candidates.map((candidate, index) => {
          const key = candidate.staged && candidate.item_id != null ? `item:${candidate.item_id}` : `entry:${candidate.f_id}`
          const selected = Boolean(choice && choice.action === 'match' && choice.key === key)
          return (
            <OptionCard
              key={key}
              selected={selected}
              disabled={disabled}
              ariaLabel={`Choose ${candidate.name || 'this candidate'}`}
              onSelect={() =>
                onChoose(item.id, {
                  action: 'match',
                  key,
                  item_id: item.id,
                  ...(candidate.staged && candidate.item_id != null
                    ? { target_item_id: String(candidate.item_id) }
                    : { f_id: candidate.f_id }),
                })
              }
            >
              <SongCandidateBody
                incoming={incoming}
                candidate={candidate}
                isNew={isNew}
                index={index}
                total={candidates.length}
                albums={songAlbums && candidate.f_id ? songAlbums[candidate.f_id] : null}
              />
            </OptionCard>
          )
        })}
        <OptionCard
          selected={Boolean(choice && choice.action === 'create')}
          disabled={disabled || !review.can_create}
          onSelect={() => onChoose(item.id, { action: 'create', item_id: item.id })}
        >
          <span className={detail.optionLabel}>Add it — it&apos;s new</span>
          {!review.can_create && (
            <span className={detail.choiceBlocked}>{canCreateBlockedReason(review, 'video', 'song')}</span>
          )}
        </OptionCard>
      </div>
    </fieldset>
  )
}

SongReviewCard.propTypes = {
  item: PropTypes.object.isRequired,
  choice: PropTypes.oneOfType([PropTypes.object]),
  onChoose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  songAlbums: PropTypes.object,
}

// Owner decision 6 (release-pairing build spec): "position by position, union
// of positions from both lists, sorted by disc then track". A position only
// one side has still gets its own row, rendered '—' on the other side, so a
// prod-only or incoming-only track is never silently dropped. Ported from
// Ingest.jsx unchanged.
function releasePositions(left, right) {
  const order = new Map()
  for (const track of [...(left.tracks || []), ...(right.tracks || [])]) {
    if (!order.has(track.position)) order.set(track.position, track)
  }
  return [...order.values()]
    .sort((a, b) => a.disc - b.disc || a.track - b.track)
    .map(({ position }) => ({
      position,
      left: (left.tracks || []).find(t => t.position === position) || null,
      right: (right.tracks || []).find(t => t.position === position) || null,
    }))
}

function ReleaseTrackComparison({ left, right, caption }) {
  const rows = releasePositions(left, right)
  const table = (
    <table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.head}>Position</th>
          <th className={classes.head}>YouTube</th>
          <th className={classes.head}>Catalogue</th>
          <th className={classes.head}>Match</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const status = !row.left || !row.left.song_f_id || !row.right || !row.right.song_f_id
            ? 'Missing'
            : row.left.song_f_id === row.right.song_f_id
              ? 'Same song'
              : 'Different song'
          return (
            <tr key={row.position} className={status !== 'Same song' ? detail.trackRowFlag : undefined}>
              <td className={classes.cell}>{row.position}</td>
              <td className={classes.cell}>
                {row.left ? (
                  <>
                    {row.left.name || 'Untitled'}
                    {row.left.song_name && row.left.song_name !== row.left.name && (
                      <span className={classes.meta}>Song: {row.left.song_name}</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className={classes.cell}>{row.right ? row.right.song_name || row.right.name || 'Untitled' : '—'}</td>
              <td className={classes.cell}>{status}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
  if (rows.length > 20) {
    return (
      <details className={detail.disclosureBody} onClick={e => e.stopPropagation()}>
        <summary className={detail.disclosureSummary}>{caption} ({rows.length} tracks)</summary>
        <div className={detail.disclosureBody}>{table}</div>
      </details>
    )
  }
  return (
    <div onClick={e => e.stopPropagation()}>
      <p className={detail.sectionNote}>{caption}</p>
      {table}
    </div>
  )
}

ReleaseTrackComparison.propTypes = { left: PropTypes.object.isRequired, right: PropTypes.object.isRequired, caption: PropTypes.string.isRequired }

const RELEASE_WHY_LABELS = Object.freeze({
  same_id: 'same id',
  same_name_and_tracks: 'same name and songs',
  same_name: 'same name, different songs',
  same_tracks: 'same songs, different name',
})

/**
 * incomingReleaseSide/candidateReleaseSide (catalogue.js) never carry
 * duration for either side - releases simply do not have that field, so no
 * row for it exists at all. Every OTHER row always renders on both sides
 * (owner correction, 2026-10-08), in the same order, even when a side's own
 * shape has nothing for it (Artist(s) - incomingReleaseSide never carries
 * one at all) - see releaseFieldRow/releaseIdFieldRow/catalogueIdFieldRow.
 */
function ReleaseCandidateBody({ incoming, candidate, index, total }) {
  const releasePageId = pickReleaseId(candidate.youtube_ids, 'MPREb_')
  const playlistId = pickReleaseId(candidate.youtube_ids, 'OLAK5uy_')
  const rows = [
    releaseFieldRow('Track count', candidate.track_count, incoming.track_count),
    releaseFieldRow('Type', candidate.album_type, incoming.release_type),
    releaseFieldRow('Year', candidate.year, incoming.year),
    releaseFieldRow(
      'Artist(s)',
      candidate.artists && candidate.artists.length ? formatArtists(candidate.artists) : candidate.artist_name
    ),
    releaseIdFieldRow('YouTube release page ID', releasePageId, releasePageId && YOUTUBE_RELEASE_PAGE(releasePageId)),
    releaseIdFieldRow('YouTube audio playlist ID', playlistId, playlistId && YOUTUBE_AUDIO_PLAYLIST(playlistId)),
    // A real id the payload sent that is neither prefix above - never
    // dropped, just not one of the two guaranteed rows, so it does not
    // disturb their fixed position.
    ...otherReleaseIds(candidate.youtube_ids).map(releaseIdRow),
    catalogueIdFieldRow(candidate.f_id),
  ]
  return (
    <>
      <div className={detail.optionHead}>
        <span className={detail.optionLabel}>{candidate.name || 'Untitled'}</span>
        <span className={detail.optionSub}>
          {total > 1 ? `Possible duplicate ${index + 1} of ${total}` : 'Possible duplicate in the catalogue'}
          {candidate.why ? ` — ${RELEASE_WHY_LABELS[candidate.why] || candidate.why}` : ''}
          {' — pick this if it’s the same; it won’t be added again'}
        </span>
      </div>
      <FieldList rows={rows} />
      <ReleaseTrackComparison
        left={incoming}
        right={candidate}
        caption={`${incoming.name || 'Incoming release'} vs ${candidate.name || 'this candidate'}, track by track`}
      />
    </>
  )
}

ReleaseCandidateBody.propTypes = { incoming: PropTypes.object.isRequired, candidate: PropTypes.object.isRequired, index: PropTypes.number.isRequired, total: PropTypes.number.isRequired }

/**
 * Same always-every-row treatment as ReleaseCandidateBody/ReleaseReviewCard
 * (owner decision, 2026-10-08): fixed row order, "Unknown"/"None" muted
 * placeholders, diff-highlighted against `prod` (the one fixed reference
 * here, same role `incoming` plays on a release_match card). `prod`
 * (candidateReleaseSide) has no Duration field, same as everywhere else
 * releases are shown. Gaps actually in this payload, not invented: an
 * `option` (incomingReleaseSide) never carries Artist(s) at all - same gap
 * release_match's own incoming side already has - and only `prod` ever has
 * a Catalogue ID; an option is a YouTube item with nothing to not-match yet.
 */
function ReleasePickOneCard({ item, choices, onPick, disabled }) {
  const review = item.review
  const prod = review.prod
  const picked = review.options.find(
    o => choices[o.item_id] && choices[o.item_id].action === 'match' && choices[o.item_id].key === `entry:${review.prod.f_id}`
  )
  const headingId = `review-${item.id}-heading`
  const prodReleasePageId = pickReleaseId(prod.youtube_ids, 'MPREb_')
  const prodPlaylistId = pickReleaseId(prod.youtube_ids, 'OLAK5uy_')
  const prodArtists = prod.artists && prod.artists.length ? formatArtists(prod.artists) : prod.artist_name
  const prodRows = [
    releaseFieldRow('Track count', prod.track_count),
    releaseFieldRow('Type', prod.album_type),
    releaseFieldRow('Year', prod.year),
    releaseFieldRow('Artist(s)', prodArtists),
    releaseIdFieldRow('YouTube release page ID', prodReleasePageId, prodReleasePageId && YOUTUBE_RELEASE_PAGE(prodReleasePageId)),
    releaseIdFieldRow('YouTube audio playlist ID', prodPlaylistId, prodPlaylistId && YOUTUBE_AUDIO_PLAYLIST(prodPlaylistId)),
    ...otherReleaseIds(prod.youtube_ids).map(releaseIdRow),
    catalogueIdFieldRow(prod.f_id),
  ]
  return (
    <fieldset className={detail.reviewItem} role="radiogroup" aria-labelledby={headingId}>
      <ReviewHead headingId={headingId} title={prod.name || 'Untitled'} badge={reviewBadge(item)} reason={review.reason} />
      <p className={detail.decisionLead}>Which of these YouTube releases is really this catalogue release? The others become their own, separate releases.</p>
      <IncomingBlock rows={prodRows} />
      {/* Same side-by-side-where-2+-fit grid as the other review kinds
          (owner correction, 2026-10-08, item 3). */}
      <div className={detail.compareGrid}>
        {review.options.map((option, index) => {
          const rows = [
            releaseFieldRow('Track count', option.track_count, prod.track_count),
            releaseFieldRow('Type', option.release_type, prod.album_type),
            releaseFieldRow('Year', option.year, prod.year),
            // incomingReleaseSide never carries artists - always "Unknown".
            releaseFieldRow('Artist(s)', null, prodArtists),
            releaseIdFieldRow('YouTube release page ID', option.source_id, option.source_id && YOUTUBE_RELEASE_PAGE(option.source_id)),
            releaseIdFieldRow('YouTube audio playlist ID', option.playlist_id, option.playlist_id && YOUTUBE_AUDIO_PLAYLIST(option.playlist_id)),
          ]
          return (
            <OptionCard
              key={option.item_id}
              selected={picked === option}
              disabled={disabled}
              onSelect={() => onPick(review, option.item_id)}
            >
              <div className={detail.optionHead}>
                <span className={detail.optionLabel}>Option{review.options.length > 1 ? ` ${index + 1} of ${review.options.length}` : ''}</span>
                <span className={detail.optionSub}>{option.name || 'Untitled'}</span>
              </div>
              <FieldList rows={rows} />
              <ReleaseTrackComparison left={option} right={prod} caption={`${option.name || 'This option'} vs ${prod.name}, track by track`} />
            </OptionCard>
          )
        })}
      </div>
    </fieldset>
  )
}

ReleasePickOneCard.propTypes = { item: PropTypes.object.isRequired, choices: PropTypes.object.isRequired, onPick: PropTypes.func.isRequired, disabled: PropTypes.bool }

function ReleaseReviewCard({ item, choice, onChoose, disabled }) {
  const review = item.review
  const incoming = review.incoming
  const candidates = review.candidates || []
  const headingId = `review-${item.id}-heading`
  // incomingReleaseSide (catalogue.js) names these two fields separately -
  // source_id (the release page / browseId) and playlist_id (the audio
  // playlist) - so unlike a candidate's flat youtube_ids[], no guessing is
  // needed here at all (owner correction, 2026-10-08). Every row always
  // renders (same helpers, same order as ReleaseCandidateBody's own rows,
  // minus Catalogue ID - an unmatched YouTube item has no catalogue id to
  // not-have, so there is nothing for that one to line up against).
  const incomingRows = [
    releaseFieldRow('Track count', incoming.track_count),
    releaseFieldRow('Type', incoming.release_type),
    releaseFieldRow('Year', incoming.year),
    releaseFieldRow('Artist(s)', null),
    releaseIdFieldRow('YouTube release page ID', incoming.source_id, incoming.source_id && YOUTUBE_RELEASE_PAGE(incoming.source_id)),
    releaseIdFieldRow('YouTube audio playlist ID', incoming.playlist_id, incoming.playlist_id && YOUTUBE_AUDIO_PLAYLIST(incoming.playlist_id)),
  ]
  return (
    <fieldset className={detail.reviewItem} role="radiogroup" aria-labelledby={headingId}>
      <ReviewHead headingId={headingId} title={incoming.name || 'Untitled'} badge={reviewBadge(item)} reason={review.reason} />
      <p className={detail.decisionLead}>Is this YouTube item already in the catalogue? Pick the duplicate, or add it as new.</p>
      {/* No separate "Source: <id>" line - item.source_id IS incoming.source_id,
          already shown labelled as "YouTube release page ID" below. */}
      <IncomingBlock rows={incomingRows} />
      {/* Same side-by-side-where-2+-fit grid as the song card (owner
          correction, 2026-10-08, item 3 - "same rules" for every kind). */}
      <div className={detail.compareGrid}>
        {candidates.map((candidate, index) => {
          const key = `entry:${candidate.f_id}`
          const selected = Boolean(choice && choice.action === 'match' && choice.key === key)
          return (
            <OptionCard
              key={key}
              selected={selected}
              disabled={disabled}
              ariaLabel={`Choose ${candidate.name || 'this candidate'}`}
              onSelect={() => onChoose(item.id, { action: 'match', key, item_id: item.id, f_id: candidate.f_id })}
            >
              <ReleaseCandidateBody incoming={incoming} candidate={candidate} index={index} total={candidates.length} />
            </OptionCard>
          )
        })}
        <OptionCard
          selected={Boolean(choice && choice.action === 'create')}
          disabled={disabled || !review.can_create}
          onSelect={() => onChoose(item.id, { action: 'create', item_id: item.id })}
        >
          <span className={detail.optionLabel}>Add it — it&apos;s new</span>
          {!review.can_create && (
            <span className={detail.choiceBlocked}>{canCreateBlockedReason(review, 'release page', 'release')}</span>
          )}
        </OptionCard>
      </div>
    </fieldset>
  )
}

ReleaseReviewCard.propTypes = { item: PropTypes.object.isRequired, choice: PropTypes.oneOfType([PropTypes.object]), onChoose: PropTypes.func.isRequired, disabled: PropTypes.bool }

/**
 * Artist candidates carry only `{f_id, name}` (catalogue.js) - no release
 * count, image, or other field on their own - so two same-named artists are
 * genuinely indistinguishable beyond what two backend PRs add, both
 * resolved by f_id and both pending merge: `data.artist_slugs` (the FAST
 * link - resolveArtistSlug falls back to writes[], then plain text) and
 * `data.artist_youtube_channels` (every YouTube channel id mapped to that
 * candidate, read below).
 *
 * `review.incoming` is frequently null - confirmed on space-4 (request
 * 52a6bcd8-era item 7720, "R3HAB"): catalogue.js's shared resolve() (the
 * function that handles this exact review, line 3586) never builds one; only
 * pairRequestedArtist's OWN review (line ~1429) does. When it is null, this
 * falls back to item.staged_data/item.source_id instead of showing "Unknown"
 * for data that is really just sitting one level up. staged_data.channel_id
 * ("official", from get_artist(credit id)) and item.source_id/staged_data.
 * source_id (YouTube's own auto-generated "Topic" channel for an artist) are
 * two DIFFERENT ids that can genuinely disagree (catalogue.js line 3591's
 * own comment) - shown as two separate rows, never merged into one. Labelled
 * "YouTube Topic channel ID" (owner correction, 2026-10-08), not "credited":
 * that is literally what YouTube itself calls this channel.
 */
function ArtistReviewCard({ item, choice, onChoose, disabled, writes, artistSlugs, artistYoutubeChannels }) {
  const review = item.review
  const incoming = review.incoming
  const staged = item.staged_data || {}
  const candidates = review.candidates || []
  const headingId = `review-${item.id}-heading`
  const incomingName = (incoming && incoming.name) || staged.name || null
  const incomingTopicId = (incoming && incoming.source_id) || item.source_id || null
  const incomingOfficialId = staged.channel_id || null
  const incomingRows = [
    releaseFieldRow('Name', incomingName),
    releaseIdFieldRow('YouTube Topic channel ID', incomingTopicId, incomingTopicId && YOUTUBE_CHANNEL(incomingTopicId)),
    releaseIdFieldRow('YouTube official channel ID', incomingOfficialId, incomingOfficialId && YOUTUBE_CHANNEL(incomingOfficialId)),
  ]
  // catalogue.js's shared resolve() (line 1429 for the requested artist,
  // :3695 for every type) sets can_create false ONLY when exact.length>0 for
  // this exact reason string, in which case review.candidates IS that exact
  // list - so its own length is the real count of catalogue artists this
  // YouTube channel already matches, and picking one of them is the only
  // way forward (owner correction, 2026-10-08 - the old lead line here never
  // said what to do).
  const decisionLead =
    review.reason === 'Multiple catalogue entries use this source identity.' && !review.can_create
      ? `This YouTube channel is linked to ${candidates.length} catalogue artists. Pick the one that really is ${incomingName || 'this artist'}.`
      : 'Is this YouTube item already in the catalogue? Pick the duplicate, or add it as new.'
  return (
    <fieldset className={detail.reviewItem} role="radiogroup" aria-labelledby={headingId}>
      <ReviewHead
        headingId={headingId}
        title={incomingName || stagedName(item) || 'Untitled'}
        badge={reviewBadge(item)}
        reason={review.reason}
      />
      <p className={detail.decisionLead}>{decisionLead}</p>
      <IncomingBlock rows={incomingRows} />
      <div className={detail.compareGrid}>
        {candidates.map((candidate, index) => {
          const key = `entry:${candidate.f_id}`
          const selected = Boolean(choice && choice.action === 'match' && choice.key === key)
          const slug = resolveArtistSlug(candidate.f_id, artistSlugs, writes)
          // Click-through (owner correction, 2026-10-08): the name and the
          // catalogue id both open the FAST page when one is resolvable,
          // same stopPropagation every other in-card link uses so it never
          // also changes the card's own selection.
          const link = text =>
            slug ? (
              <Link className={detail.idRowLink} to={`/artist/${slug}`} target="_blank" onClick={e => e.stopPropagation()}>
                {text}
              </Link>
            ) : (
              text
            )
          const channelIds = (artistYoutubeChannels && artistYoutubeChannels[candidate.f_id]) || []
          // The row is highlighted when one of THIS candidate's own channel
          // ids is one of the incoming ids above - that shared id is what
          // caused the conflict in the first place, not a mismatch to flag.
          const channelMatchesIncoming = channelIds.some(id => id === incomingTopicId || id === incomingOfficialId)
          const channelRow = channelIds.length
            ? {
                label: 'YouTube channel ID',
                value: channelIds.map((id, i) => (
                  <React.Fragment key={id}>
                    {i > 0 && ', '}
                    <a
                      className={detail.idRowLink}
                      href={YOUTUBE_CHANNEL(id)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                    >
                      {id}
                    </a>
                  </React.Fragment>
                )),
                diff: channelMatchesIncoming,
              }
            : releaseIdFieldRow('YouTube channel ID', null, null)
          const rows = [
            {
              label: 'Name',
              value: candidate.name ? link(candidate.name) : <span className={detail.placeholderValue}>Unknown</span>,
              diff: candidate.name != null && incomingName != null && String(candidate.name) !== String(incomingName),
            },
            {
              label: 'Catalogue ID',
              value: candidate.f_id ? link(candidate.f_id) : <span className={detail.placeholderValue}>None</span>,
            },
            {
              label: 'FAST page',
              value: slug ? link('Open in FAST') : <span className={detail.placeholderValue}>None</span>,
            },
            channelRow,
          ]
          return (
            <OptionCard
              key={key}
              selected={selected}
              disabled={disabled}
              ariaLabel={`Choose ${candidate.name || 'this candidate'}`}
              onSelect={() => onChoose(item.id, { action: 'match', key, item_id: item.id, f_id: candidate.f_id })}
            >
              <div className={detail.optionHead}>
                <span className={detail.optionLabel}>{candidate.name || 'Untitled'}</span>
                <span className={detail.optionSub}>
                  {candidates.length > 1 ? `Possible duplicate ${index + 1} of ${candidates.length}` : 'Possible duplicate in the catalogue'}
                  {' — pick this if it’s the same; it won’t be added again'}
                </span>
              </div>
              <FieldList rows={rows} />
            </OptionCard>
          )
        })}
        {/* "Add it" joins the same grid as the candidates (owner correction,
            2026-10-08, item 3: side by side wherever 2+ fit) rather than
            sitting full-width below it. */}
        <OptionCard
          selected={Boolean(choice && choice.action === 'create')}
          disabled={disabled || !review.can_create}
          onSelect={() => onChoose(item.id, { action: 'create', item_id: item.id })}
        >
          <span className={detail.optionLabel}>Add it — it&apos;s new</span>
          {!review.can_create && (
            <span className={detail.choiceBlocked}>{canCreateBlockedReason(review, 'channel', 'artist')}</span>
          )}
        </OptionCard>
      </div>
    </fieldset>
  )
}

ArtistReviewCard.propTypes = {
  item: PropTypes.object.isRequired,
  choice: PropTypes.oneOfType([PropTypes.object]),
  onChoose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  writes: PropTypes.array,
  artistSlugs: PropTypes.object,
  artistYoutubeChannels: PropTypes.object,
}

/** Official-video plan, redesign A: 2 choices only - "use this video" (with
 * an editable intro-gap field) or "keep the art track". Never a catalogue
 * match/create, so it never joins the generic candidate flow above. */
/**
 * The official-video review (catalogue.js officialVideoCommitDecisions,
 * ~line 921) writes exactly: song_f_id, song_name, art_track_title,
 * youtube_title, release_page_id, art_length_seconds, video_length_seconds,
 * channel_name, rule, outcome, shared, length_difference_seconds,
 * default_gap_ms. There is NO `art_track_id` field on the review itself -
 * the art track's id is the song item's own `source_id` (confirmed against
 * officialVideoCommitDecisions' own lookup, which keys
 * releaseVideoByArtTrackId off `p.item.source_id` before this review is ever
 * built). All of those names were already correct here; what was actually
 * missing was an honest placeholder - a bare `|| '—'` em dash for a null
 * length/title read as "blank" in a screenshot. Every row below now uses
 * the same "Unknown"/"None" placeholder convention the release cards use.
 */
function VideoReviewCard({ item, choice, onChoose, disabled }) {
  const review = item.review
  const usingVideo = typeof choice === 'string' && choice.startsWith('video_use:')
  const gapText = usingVideo ? choice.slice('video_use:'.length) : String(review.default_gap_ms ?? 0)
  const artTrackId = review.art_track_id || item.source_id
  const videoId = review.release_page_id
  const headingId = `review-${item.id}-heading`
  const videoReason = review.shared
    ? 'This release video is also claimed by another song — it is never swapped in automatically.'
    : review.outcome === 'yes_card'
      ? `Official video, but its length differs from the art track by ${Math.abs(Math.round(review.length_difference_seconds ?? 0))} s.`
      : `The title does not clearly say "official video" (rule: ${review.rule}).`

  const videoLength = seconds(review.video_length_seconds)
  const artLength = seconds(review.art_length_seconds)
  const titleRow = releaseFieldRow('Title', review.youtube_title, review.art_track_title)
  const artTitleRow = releaseFieldRow('Title', review.art_track_title, review.youtube_title)
  const videoLengthRow = releaseFieldRow('Length', videoLength, artLength)
  const artLengthRow = releaseFieldRow('Length', artLength, videoLength)
  // Owner decision, 2026-10-08: the backend is moving default_gap_ms to
  // always 0 in a separate PR, so the field itself no longer carries a
  // suggested value - length_difference_seconds (still sent) becomes a
  // separate, plain-words HINT instead of something silently pre-filled.
  // Omitted when 0 or missing - a 0 s difference is not a hint worth giving.
  const lengthDiffSeconds = typeof review.length_difference_seconds === 'number' && Number.isFinite(review.length_difference_seconds)
    ? Math.round(review.length_difference_seconds)
    : null
  const lengthDiffHint = lengthDiffSeconds
    ? `Video is ${Math.abs(lengthDiffSeconds)} s ${lengthDiffSeconds > 0 ? 'longer' : 'shorter'} than the art track.`
    : null

  function selectUseVideo() {
    if (!disabled) onChoose(item.id, `video_use:${gapText}`)
  }

  return (
    <fieldset className={detail.reviewItem} role="radiogroup" aria-labelledby={headingId}>
      <ReviewHead headingId={headingId} title={review.song_name || stagedName(item) || 'Untitled'} badge={reviewBadge(item)} reason={videoReason} />
      <p className={detail.decisionLead}>Use this video, or keep the art track? Click a card to choose.</p>
      <div className={detail.compareGrid}>
        <OptionCard selected={usingVideo} disabled={disabled} onSelect={selectUseVideo}>
          <span className={detail.optionLabel}>Use this video</span>
          <YoutubeFrame id={videoId} title="Official video" />
          <FieldList rows={[titleRow, videoLengthRow, releaseIdFieldRow('Video ID', videoId, videoId && YOUTUBE_WATCH(videoId))]} />
          <div className={usingVideo ? detail.gapField : `${detail.gapField} ${detail.gapFieldInactive}`}>
            <label htmlFor={`video-gap-${item.id}`} className={detail.gapLabel}>
              Intro gap
            </label>
            <div className={detail.gapInputRow}>
              <input
                id={`video-gap-${item.id}`}
                className={detail.gapInput}
                type="number"
                step="1"
                disabled={disabled}
                value={gapText}
                onFocus={selectUseVideo}
                onChange={event => onChoose(item.id, `video_use:${event.target.value}`)}
              />
              <span className={detail.gapUnit}>ms</span>
            </div>
            <p className={detail.gapHelp}>Starts at 0. Set it only if the video has an intro before the song.</p>
            {lengthDiffHint && <p className={detail.gapHelp}>{lengthDiffHint}</p>}
          </div>
        </OptionCard>
        <OptionCard selected={choice === 'video_keep'} disabled={disabled} onSelect={() => onChoose(item.id, 'video_keep')}>
          <span className={detail.optionLabel}>Keep the art track</span>
          <YoutubeFrame id={artTrackId} title="Art track" />
          <FieldList rows={[artTitleRow, artLengthRow, releaseIdFieldRow('Art track ID', artTrackId, artTrackId && YOUTUBE_WATCH(artTrackId))]} />
        </OptionCard>
      </div>
    </fieldset>
  )
}

VideoReviewCard.propTypes = { item: PropTypes.object.isRequired, choice: PropTypes.string, onChoose: PropTypes.func.isRequired, disabled: PropTypes.bool }

/** Dispatches one open review to its kind-specific card. Every kind the
 * backend can send is listed here explicitly (API-PAYLOAD.md's own review
 * vocabulary); an unlisted item_type/kind combination is a gap, not a
 * silent fallback, so it is deliberately left to fall through to
 * SongReviewCard's generic song shape rather than crash. */
function ReviewCard({ item, choice, choices, onChoose, onPickOne, disabled, writes, artistSlugs, artistYoutubeChannels, songAlbums }) {
  if (item.item_type === 'release' && item.review.kind === 'release_pick_one') {
    return <ReleasePickOneCard item={item} choices={choices} onPick={onPickOne} disabled={disabled} />
  }
  if (item.item_type === 'release') {
    return <ReleaseReviewCard item={item} choice={choice} onChoose={onChoose} disabled={disabled} />
  }
  if (item.item_type === 'artist') {
    return (
      <ArtistReviewCard
        item={item}
        choice={choice}
        onChoose={onChoose}
        disabled={disabled}
        writes={writes}
        artistSlugs={artistSlugs}
        artistYoutubeChannels={artistYoutubeChannels}
      />
    )
  }
  if (item.item_type === 'song' && item.review.kind === 'video') {
    return <VideoReviewCard item={item} choice={choice} onChoose={onChoose} disabled={disabled} />
  }
  return <SongReviewCard item={item} choice={choice} onChoose={onChoose} disabled={disabled} songAlbums={songAlbums} />
}

ReviewCard.propTypes = {
  item: PropTypes.object.isRequired,
  choice: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  choices: PropTypes.object.isRequired,
  onChoose: PropTypes.func.isRequired,
  onPickOne: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  writes: PropTypes.array,
  artistSlugs: PropTypes.object,
  artistYoutubeChannels: PropTypes.object,
  songAlbums: PropTypes.object,
}

/**
 * One catalogue write, its own nested disclosure - collapsed by default,
 * same as the section around it. The Field/Before/After table (Ingest.jsx's
 * own Audit component) is built only once this row is actually opened -
 * `useState` + `onToggle`, not just CSS-hidden - because a large sync can
 * produce 400+ of these and building every table up front is the real cost,
 * not just an unseen one. Ingest.jsx's own filter (`before !== after`) is
 * kept exactly: on an insert, before_data is empty, so every field in
 * after_data differs and all of them show - that IS "every field main
 * shows" for a Created row; on an update, only the fields that actually
 * changed are shown, and since every shown row on an update is by
 * definition a changed one, all of them get the diff highlight.
 */
function WriteRow({ write }) {
  const [opened, setOpened] = useState(false)
  const afterData = write.after_data || {}
  const beforeData = write.before_data || {}
  const fields = opened
    ? Object.keys(afterData).filter(key => JSON.stringify(beforeData[key]) !== JSON.stringify(afterData[key]))
    : []
  return (
    <details className={detail.writeRow} onToggle={event => setOpened(event.target.open)}>
      <summary className={detail.writeSummary}>
        {write.operation === 'insert' ? 'Created' : 'Updated'} {write.table_name} ·{' '}
        <code className={detail.writeKey}>{auditValue(write.row_pk)}</code>
      </summary>
      {opened && (
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.head}>Field</th>
              <th className={classes.head}>Before</th>
              <th className={classes.head}>After</th>
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 && (
              <tr>
                <td className={classes.cell} colSpan={3}>
                  No field differs from before.
                </td>
              </tr>
            )}
            {fields.map(key => (
              <tr key={key} className={write.operation === 'update' ? detail.trackRowFlag : undefined}>
                <td className={classes.cell}>{key}</td>
                <td className={classes.cell}>
                  <code className={detail.auditValue}>{auditValue(beforeData[key])}</code>
                </td>
                <td className={classes.cell}>
                  <code className={detail.auditValue}>{auditValue(afterData[key])}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  )
}

WriteRow.propTypes = { write: PropTypes.object.isRequired }

// ---------------------------------------------------------------------------
// "Warnings (N)" - every per-item problem/warning this page shows inline
// elsewhere (cover art, song videos, lyrics - see CompletionSummary.jsx),
// plus two kinds main's old Ingest.jsx shows that this page did not show
// ANYWHERE before this (backfill warnings, a release/playlist mismatch),
// gathered into one collapsed section so none of them only live buried
// inside "What this sync did". Nothing already shown is removed - this is
// in addition. Request-level stop errors (the "Stopped" section for
// needs_attention) are untouched and NOT duplicated in here. Named
// "Warnings", not "Problems" (owner correction, 2026-10-08): none of these
// stopped the sync.
// ---------------------------------------------------------------------------

// Ported from Ingest.jsx (not imported - these two pages are independent
// readings of the same backend contract, and a copy here is more honest
// than a cross-page dependency between them). Keep in sync if Ingest.jsx's
// own list ever changes.
const BACKFILL_WARNING_PREFIXES = [
  'backfill-bad-id:',
  'backfill-video-unavailable:',
  'backfill-video-no-artists:',
  'backfill-video-unplayable:',
  'backfill-video-disagree:',
  'backfill-channel-unavailable:',
]

function isBackfillWarning(event) {
  return event.kind === 'warning' && BACKFILL_WARNING_PREFIXES.some(prefix => event.event_key?.startsWith(prefix))
}

function backfillWarningSongFID(event) {
  return (event.details && event.details.song_f_id) || null
}

/** Same five shapes as Ingest.jsx's own inline rendering, worded as a row
 * message rather than a full sentence (the row's own "<item name> —" prefix
 * already names the prod song, where one applies). */
function backfillWarningReason(event) {
  const d = event.details || {}
  if (event.event_key.startsWith('backfill-video-disagree:')) {
    return `Has ${d.video_source_ids?.length ?? 0} ${d.music_video_type} videos (${(d.video_source_ids || []).join(', ')}) with different authors or artists. Its artists and display name were not changed.`
  }
  if (event.event_key.startsWith('backfill-channel-unavailable:')) {
    return `Credited artist channel ${d.source_id}: ${event.message}`
  }
  if ('bad_id' in d) {
    return `Stored video id ${d.bad_id} is not a valid YouTube video id. It was not fetched.`
  }
  if (event.event_key.startsWith('backfill-video-unplayable:')) {
    return `Video ${d.source_id} is unplayable on YouTube (status ${d.playability_status}). Its YouTube data was still saved and used as evidence. Check the video and replace it if needed.`
  }
  if (event.event_key.startsWith('backfill-video-no-artists:')) {
    return `Video ${d.source_id} has no artist data (its YouTube watch playlist is empty or starts with another video). Its artists were not saved and are not used as evidence.`
  }
  return `Video ${d.source_id} could not be fetched (${d.code}). It was not backfilled and gives no evidence.`
}

/** A warning row's own link, by the WARNING's own `kind` vocabulary
 * (`album`/`artist`/`song` - cover_art/song_videos warnings, API-PAYLOAD.md),
 * never an ingest item_type (`release`/`artist`/`song`) - the two are spelled
 * differently for the same idea. `kind: 'request'` never reaches here (its
 * f_id is the request's own uuid, never a catalogue id - API-PAYLOAD.md). */
function warningHref(kind, fId, artistSlugs, writes) {
  if (!fId) return null
  if (kind === 'album') return `/songs/album/${fId}`
  if (kind === 'song') return `/song/${fId}`
  if (kind === 'artist') {
    const slug = resolveArtistSlug(fId, artistSlugs, writes)
    return slug ? `/artist/${slug}` : null
  }
  return null
}

function buildWarningGroups(request, events) {
  const result = request.result || null
  const art = (result && result.cover_art) || null
  const video = (result && result.song_videos) || null
  const lyrics = (result && result.lyrics) || null
  const artWarnings = (art && art.warnings) || []
  const videoWarnings = (video && video.warnings) || []
  const lyricsWarnings = (lyrics && lyrics.warnings) || []

  const coverArtRows = artWarnings.map((w, i) => ({
    key: `art-${i}`,
    fId: w.kind === 'request' ? null : w.f_id,
    kind: w.kind,
    message: w.kind === 'request' ? requestArtReason(w) : artReason(w),
  }))
  const videoRows = videoWarnings.map((w, i) => ({
    key: `video-${i}`,
    fId: w.kind === 'request' ? null : w.f_id,
    kind: 'song',
    message: w.kind === 'request' ? requestVideoReason(w) : videoReason(w),
  }))
  // LYRICS_DISABLED/LYRICS_BAD_SONG_DATA already have their own prominent
  // notices in CompletionSummary's LyricsSummary - not duplicated as rows
  // here, same "never shown twice" rule the ID-row work used earlier.
  const lyricsRows = lyricsWarnings
    .filter(w => w.code !== 'LYRICS_DISABLED' && w.code !== 'LYRICS_BAD_SONG_DATA')
    .map((w, i) => ({ key: `lyrics-${i}`, fId: null, kind: null, message: lyricsWarningReason(w) }))
  // Not shown anywhere on this page before (Ingest.jsx's own backfillWarnings).
  const backfillRows = (events || []).filter(isBackfillWarning).map(event => ({
    key: event.id,
    fId: backfillWarningSongFID(event),
    kind: 'song',
    message: backfillWarningReason(event),
  }))
  // Also new here: request.last_error's own RELEASE_PLAYLIST_MISMATCH code
  // (Ingest.jsx's own releaseMismatchWarning) - the request-level "Stopped"
  // section already shows request.last_error generically when needs_attention;
  // this is an ADDITIONAL, specifically-labelled surfacing, never a
  // replacement for that section.
  const releaseMismatchRows =
    request.last_error && request.last_error.code === 'RELEASE_PLAYLIST_MISMATCH'
      ? [{ key: 'release-mismatch', fId: null, kind: null, message: `${request.last_error.message} (${request.last_error.code})` }]
      : []

  return [
    { label: 'Cover art', rows: coverArtRows },
    { label: 'Song videos', rows: videoRows },
    { label: 'Lyrics', rows: lyricsRows },
    { label: 'Backfill', rows: backfillRows },
    { label: 'Release mismatch', rows: releaseMismatchRows },
  ]
}

/**
 * One collapsed section, every known warning kind grouped under its own
 * sub-heading - a group with zero rows is simply not shown, same as every
 * other "(N)" section on this page. Named "Warnings", not "Problems" (owner
 * correction, 2026-10-08): none of these stopped the sync, which the
 * subtext line under the title says in plain words.
 */
function WarningsSection({ groups, items, writes, artistSlugs }) {
  const shown = groups.filter(g => g.rows.length > 0)
  const total = shown.reduce((sum, g) => sum + g.rows.length, 0)
  if (!total) return null
  return (
    <details id="warnings" className={detail.section}>
      <summary className={detail.disclosureSummary}>Warnings ({total})</summary>
      <div className={detail.disclosureBody}>
        <p className={detail.sectionNote}>These did not stop the sync. Most can be ignored without issue.</p>
        {shown.map(group => (
          <div key={group.label} className={detail.summaryBlock}>
            <h3 className={detail.summaryHeading}>
              {group.label} ({group.rows.length})
            </h3>
            <ul className={detail.plainList}>
              {group.rows.map(row => {
                const name = row.fId ? itemNameOf(items, row.fId) : null
                const href = row.fId && row.kind ? warningHref(row.kind, row.fId, artistSlugs, writes) : null
                return (
                  <li key={row.key}>
                    {name ? (
                      href ? <Link to={href}>{name}</Link> : <strong>{name}</strong>
                    ) : row.fId ? (
                      <code className={detail.code}>{row.fId}</code>
                    ) : null}
                    <span className={detail.summaryWhere}>
                      {name || row.fId ? ' — ' : ''}
                      {row.message}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </details>
  )
}

WarningsSection.propTypes = {
  groups: PropTypes.array.isRequired,
  items: PropTypes.array.isRequired,
  writes: PropTypes.array,
  artistSlugs: PropTypes.object,
}

export default function RequestDetail() {
  const { id } = useParams()
  // DEV ONLY: /ingest/<state>?mock=1 renders a fixture, for designing against
  // states this environment cannot produce. Delete with __mock__.
  const [params] = useSearchParams()
  const mock = mockRequest(id, params.get('mock'))
  const { getIngestRequest, tendIngestRequest } = useAPI()
  const queryClient = useQueryClient()
  const { getCurrentUser } = useAuth()
  const currentUser = getCurrentUser()
  const adminNames = useAdminUserNames()
  const [choices, setChoices] = useState({})
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(null)

  const query = useQuery({
    queryKey: ['ingest-request', id],
    queryFn: () => getIngestRequest(id),
    enabled: Boolean(id) && !mock,
    // The page shows its own error; the global handler would toast it too.
    meta: { suppressGlobalError: true },
    // The app-wide default (src/index.jsx) is staleTime: Infinity and
    // refetchOnWindowFocus: false, so without this a tab that already
    // stopped polling below (status needs_review/needs_attention, nothing
    // of ours still pending) would never fetch again on its own - not on
    // remount, not on refocus - and would keep showing whatever item count
    // this request had the moment it stopped, even after someone answers a
    // review from a DIFFERENT tab or page and the request moves on. 'always'
    // only forces the ONE fetch a fresh mount already does elsewhere in the
    // app; it does not change the refetchInterval policy below.
    refetchOnMount: 'always',
    // Stop when settled AND when the request is waiting on a person -
    // needs_review/needs_attention change only when somebody acts, and every
    // poll costs the server a Temporal sweep (http.js kick + runtime).
    retry: false,
    refetchInterval: q => {
      if (q.state.status === 'error') return false
      const request = (q.state.data && q.state.data.request) || null
      if (!request || isSettled(request.status)) return false
      // An action bumps `revision` and the workflow acts on it later, so a
      // request waiting on us is still worth watching once we have acted.
      const pending = request.revision > request.delivered_revision || request.cancel_requested ||
        (request.status === 'needs_review' && actionNotYetResumed(q.state.data.events))
      if (needsAPerson(request.status) && !pending) return false
      return POLL_MS
    },
  })

  const data = mock || query.data || {}
  const request = data.request || {}
  const items = useMemo(() => data.items || [], [data.items])
  // What "Releases and songs" counts and lists - see filterCatalogueItems.
  const catalogueItems = useMemo(() => filterCatalogueItems(items), [items])
  const historyEntries = useMemo(() => summarise(data.events || []), [data.events])
  const reviews = useMemo(() => items.filter(i => i.review), [items])
  // revision > delivered_revision means the workflow has not picked up the
  // last action yet (client.js), so pressing again only re-signals - hoisted
  // up here (from its old home next to `stale`/`actionsDisabled` below)
  // because waitingForPerson needs it too. Also true while the workflow has
  // the signal but has not moved off needs_review yet (actionNotYetResumed).
  const undelivered = request.revision > request.delivered_revision || request.cancel_requested ||
    (request.status === 'needs_review' && actionNotYetResumed(data.events))
  /**
   * Owner rule (2026-10-09): "if the backend gives a question I don't think
   * the frontend should ever hide it," restated the same day as "make sure
   * the frontend never hides a decision. It shows what the backend tells it
   * to." While the request is genuinely waiting on a person - status
   * needs_review and nothing of ours still pending - every item carrying a
   * review is an open question, full stop. There is deliberately NO
   * "already answered" heuristic gating visibility any more: store.js can
   * re-raise a review on an item that still carries a stale, no-longer-
   * fitting decision from an earlier round (the real failure on space-4
   * request e301dfc9-6181-49b8-8eaa-0f6d9d45be92: items 7907/7910 each had a
   * fresh review AND an old decision, and the old isReviewAnswered-based
   * filter hid both, leaving the page saying "Nothing else is needed" while
   * the backend waited). The only two groupings this page still makes are
   * rendering-only, never visibility-hiding: release_pick_one's dedupe (one
   * card speaks for the whole group - see pickOneGroupKey) and routing the
   * two genuinely engineer-only artist shapes (id_conflict/no_name - the
   * server 409s both a match and a create for them) to their own read-only
   * list instead of a decision card. Both exceptions still SHOW the item;
   * neither ever drops it off the page.
   */
  const waitingForPerson = request.status === 'needs_review' && !undelivered
  const unanswerableArtistReviews = useMemo(
    () => reviews.filter(i => i.item_type === 'artist' && (i.review.id_conflict || i.review.no_name)),
    [reviews]
  )
  const openReviews = useMemo(() => {
    if (!waitingForPerson) return []
    const seenPickOneGroups = new Set()
    return reviews.filter(i => {
      if (i.item_type === 'artist' && (i.review.id_conflict || i.review.no_name)) return false
      // release_pick_one (build spec "Store decisions"): the backend writes
      // the SAME review onto every option's own ingest_item in the group, so
      // without this a group of N YouTube releases resolved to one catalogue
      // release would draw N near-identical "pick one of these" cards and
      // count as N decisions, instead of the one choice it actually is.
      // Deduplicated by the catalogue release plus the group's own sorted
      // option ids - see pickOneGroupKey. This is the one dedupe the owner
      // rule above still allows - a rendering grouping, never a hide.
      if (i.item_type === 'release' && i.review.kind === 'release_pick_one') {
        const key = pickOneGroupKey(i.review)
        if (seenPickOneGroups.has(key)) return false
        seenPickOneGroups.add(key)
      }
      return true
    })
  }, [reviews, waitingForPerson])
  // "Your decisions are saved" only makes sense while nothing is actually
  // open for the operator to act on right now - i.e. NOT waiting on a person
  // (mid-flight just after SEND, or needs_attention/settled). While waiting,
  // every answer lives on its own card instead (see openReviews above and
  // effectiveChoice below), never duplicated into this list too.
  const answered = useMemo(() => (waitingForPerson ? [] : reviews.filter(isReviewAnswered)), [reviews, waitingForPerson])

  /**
   * The grey status area's headline only means something while Temporal
   * reports a live activity, and `data.runtime.activities` is legitimately
   * EMPTY for the (frequent, normal) moments between two activities - e.g.
   * the instant after a `reportProgress` write and before the next fetchNode
   * call starts (workflows.js's own `progress()` helper is itself a
   * short-lived activity). Gating the whole area on that list's length made
   * it blink off and back on every few polls, which is what "appearing and
   * disappearing" was. This ref remembers the last non-empty headline/step/
   * retry info across polls, so a poll that lands in one of those gaps keeps
   * showing what was true a moment ago instead of blanking the area -
   * mutated directly during render (not in an effect) so the SAME render
   * that received the new poll shows it, with no one-render lag. Only a
   * genuinely different activity or step ever replaces a field; a gap never
   * clears one.
   */
  const lastStatusRef = useRef({ headline: null, step: null, attempt: null, nextRetry: null })
  const liveActivity = (data.runtime && data.runtime.activities && data.runtime.activities[0]) || null
  if (liveActivity) {
    lastStatusRef.current.headline = ACTIVITY_NAMES[liveActivity.type] || liveActivity.type
    lastStatusRef.current.attempt = liveActivity.attempt
    lastStatusRef.current.nextRetry = liveActivity.next_retry
  }
  if (request.step) lastStatusRef.current.step = request.step

  async function act(action, decisions) {
    setBusy(true)
    try {
      await tendIngestRequest(id, action, decisions)
      await queryClient.invalidateQueries({ queryKey: ['ingest-request', id] })
      queryClient.invalidateQueries({ queryKey: ['ingest-requests'] })
      successToast(
        action === 'review' ? 'Decisions sent. The synchronization continues.' :
        action === 'retry' ? 'Retrying.' : 'Cancellation requested.'
      )
      if (action === 'review') setChoices({})
    } catch (err) {
      errorToast((err && err.message) || 'That did not go through.')
    }
    setBusy(false)
  }

  if (!mock && (query.isLoading || (query.isError && !query.data))) {
    return (
      <div className={classes.shell}>
        <div className={classes.header}>
          <div className={detail.heading}>
            <Link to="/ingest" className={detail.back}>
              View all requests
            </Link>
            <h1 className={classes.title}>Artist request</h1>
          </div>
        </div>
        <div className={classes.body}>
          {query.isLoading ? (
            <p className={classes.empty}>Loading…</p>
          ) : (
            <p className={classes.empty}>
              {query.error?.message || 'We could not load this request.'}{' '}
              <button type="button" className={classes.linkButton} onClick={() => query.refetch()}>
                Try again
              </button>
            </p>
          )}
        </div>
      </div>
    )
  }

  // A video review's choice is a plain string ('video_keep' or
  // 'video_use:<gap_ms>'), never the {action,key,...} object every other
  // review kind stores in `choices` - so "decided" for one also needs
  // videoChoiceValid (an empty or non-numeric gap is not a real choice yet).
  //
  // effectiveChoice reads a local pick first and only falls back to
  // decisionFitsReview's hint when nothing has been clicked this session, so
  // a poll landing between two keystrokes can never clobber an answer the
  // operator already gave - and the hint still goes through SEND like any
  // other pick (owner item 2: a hint only, never auto-submitted).
  function effectiveChoice(item) {
    return choices[item.id] !== undefined ? choices[item.id] : decisionFitsReview(item)
  }
  /**
   * A release_pick_one card counts as decided only when `choices` answers
   * the review this item CURRENTLY carries, not merely "some choice exists
   * for this item's own id" - `choices` is only cleared after a
   * successfully SENT review (setChoices({}) above, action === 'review'),
   * never after RECHECK MATCHING (act('retry')). So a stale pick from an
   * earlier round of the same group can survive into a freshly re-raised
   * group with a different membership (store.js can swap a sibling out,
   * e.g. {A,B} -> {A,C}) or a different prod.f_id. Left unchecked, that goes
   * wrong two ways: a sibling with no choice at all (pickOneGroupDecisions
   * below reads `choices[item_id]` for every options[].item_id, including
   * ones nothing has ever written - `undefined` - and nonVideoDecisions'
   * own filter then throws reading `.action` off it, blanking the whole
   * page), or every sibling still answered but against the OLD prod.f_id (a
   * stale match the server 409s on arrival). Decided only when every
   * options[].item_id has a choice, exactly one is {action:'match', f_id:
   * review.prod.f_id}, and every other is {action:'create'} - anything
   * else, however `choices` got populated, is not decided yet.
   */
  function isPickOneDecided(review) {
    let matches = 0
    for (const option of review.options) {
      const choice = choices[option.item_id]
      if (!choice) return false
      if (choice.action === 'match') {
        if (choice.key !== `entry:${review.prod.f_id}`) return false
        matches += 1
      } else if (choice.action !== 'create') {
        return false
      }
    }
    return matches === 1
  }
  // Single entry point for "has this card been decided": every other kind
  // still goes through isReviewDecided/effectiveChoice exactly as before;
  // only release_pick_one needs the whole-group check above, since
  // isReviewDecided only ever sees this ONE item's own choice.
  function isDecided(item) {
    return item.item_type === 'release' && item.review.kind === 'release_pick_one'
      ? isPickOneDecided(item.review)
      : isReviewDecided(item, effectiveChoice(item))
  }
  const decided = openReviews.filter(isDecided).length
  const sending = openReviews.filter(isDecided)
  const nonVideoSending = sending.filter(i => i.review.kind !== 'video')
  /**
   * release_pick_one (store.js ~1196): the same review sits on EVERY
   * option's own ingest_item, and the server 409s ("Pick exactly one
   * release...") unless a decision for every options[].item_id arrives in
   * the same submission - but openReviews dedupes the whole group down to
   * the ONE item that gets its own card (pickOneGroupKey's own comment), so
   * `choice` here is only ever that one item's own decision. onPickOne
   * already wrote a decision for every option into `choices` the moment the
   * operator picked one, so this just reads the rest of the group back out
   * of `choices` instead of leaving them unsent.
   */
  function pickOneGroupDecisions(item, choice) {
    return item.review.options.map(option => (option.item_id === item.id ? choice : choices[option.item_id]))
  }
  // Counted per real catalogue decision, not per card: a decided pick-one
  // card is ONE decision for `decided` above, but resolves to 1 match + N-1
  // creates, and the confirm dialog's counts should say so rather than
  // always showing a lone "1 will be merged" for the whole group.
  const nonVideoDecisions = nonVideoSending.flatMap(i => {
    const choice = effectiveChoice(i)
    return i.review.kind === 'release_pick_one' ? pickOneGroupDecisions(i, choice) : [choice]
  })
  const merges = nonVideoDecisions.filter(d => d.action === 'match').length
  const creates = nonVideoDecisions.length - merges
  const videoDecisions = sending.length - nonVideoSending.length

  function sendDecisions() {
    act(
      'review',
      sending.flatMap(i => {
        const choice = effectiveChoice(i)
        // Official-video plan: video_use/video_keep decode from their own
        // string encoding, never from the {action,key,...} shape every other
        // kind uses - a video review is never a catalogue match or create.
        if (i.review.kind === 'video') {
          return [choice === 'video_keep'
            ? { item_id: i.id, action: 'video_keep' }
            : { item_id: i.id, action: 'video_use', gap_ms: Number(choice.slice('video_use:'.length)) }]
        }
        const decisions = i.review.kind === 'release_pick_one' ? pickOneGroupDecisions(i, choice) : [choice]
        return decisions.map(({ key, ...decision }) => decision)
      })
    )
  }

  /**
   * Release pick-one (build spec "Store decisions"): picking one option
   * writes a decision for EVERY option.item_id in the group in one state
   * update - the picked one gets a real match against the catalogue release,
   * every other option gets 'create' (each becomes its own, separate
   * release) - matching main's own setPickOneDecision in Ingest.jsx.
   */
  function onPickOne(review, pickedItemId) {
    setChoices(prev => {
      const next = { ...prev }
      for (const option of review.options) {
        next[option.item_id] = option.item_id === pickedItemId
          ? { action: 'match', key: `entry:${review.prod.f_id}`, item_id: option.item_id, f_id: review.prod.f_id }
          : { action: 'create', key: 'create', item_id: option.item_id }
      }
      return next
    })
  }
  // A superseded generation makes every action a guaranteed 409, so the page
  // must not offer them (store.js locked() -> STALE_GENERATION).
  const stale = data.current_generation === false
  // `undelivered` itself is hoisted up next to waitingForPerson, above.
  const actionsDisabled = busy || stale || undelivered
  const artistItem = items.find(i => i.item_type === 'artist')
  const artistName = stagedName(artistItem)
  // See resolveArtistSlug's own comment: data.artist_slugs (pending backend
  // PR) first, then the writes-based fallback, then plain text.
  const requestArtistSlug = resolveArtistSlug(request.artist_f_id, data.artist_slugs, data.writes)
  const warningGroups = buildWarningGroups(request, data.events)

  return (
    <div className={classes.shell}>
      <div className={classes.header}>
        <div className={detail.heading}>
          <Link to="/ingest" className={detail.back}>
            View all requests
          </Link>
          <h1 className={classes.title}>
            <span className={detail.titleKind}>Artist request:</span>{' '}
            {/* The name once the artist has been fetched; the id until then.
                The ids stay in the facts row either way. */}
            {artistName || <code className={detail.titleId}>{request.source_id}</code>}
          </h1>
          <span
            className={`${classes.badge} ${classes['tone_' + statusTone(request)] || ''}`}
            aria-live="polite"
          >
            {statusLabel(request)}
          </span>
        </div>
        <div className={detail.headerRight}>
          {!isSettled(request.status) && (
            <div className={detail.headerActions}>
              {(request.status === 'needs_attention' || request.status === 'needs_review') && (
                <ButtonOutline
                  color="green"
                  size="small"
                  disable={actionsDisabled}
                  text={request.status === 'needs_attention' ? 'RETRY' : 'RECHECK MATCHING'}
                  onClick={() => act('retry')}
                />
              )}
              <ButtonOutline
                color="pink"
                size="small"
                disable={actionsDisabled}
                text={request.cancel_requested ? 'CANCELLING…' : 'CANCEL REQUEST'}
                onClick={() => setConfirming('cancel')}
              />
            </div>
          )}
        </div>
      </div>

      <div className={classes.body}>
        <dl className={detail.facts}>
          <div>
            <dt>Asked by</dt>
            <dd title={request.requested_by || undefined}>
              {requesterName(request.requested_by, { currentUser, adminNames }) || request.requested_by || '—'}
            </dd>
          </div>
          <div><dt>Started</dt><dd>{when(request.date_created)}</dd></div>
          <div><dt>{isSettled(request.status) ? 'Finished' : 'Last progress'}</dt>
            <dd>{when(request.date_finished || request.date_updated)}</dd></div>
          <div><dt>Step</dt><dd>{request.step || '—'}</dd></div>
          <div>
            <dt>Artist YouTube ID</dt>
            <dd>
              <a
                className={detail.idLink}
                href={`https://music.youtube.com/channel/${request.source_id}`}
                target="_blank"
                rel="noreferrer"
              >
                <code>{request.source_id}</code>
              </a>
            </dd>
          </div>
          {request.artist_f_id && (
            <div>
              <dt>Feels artist ID</dt>
              <dd>
                {requestArtistSlug ? (
                  <Link className={detail.idLink} to={`/artist/${requestArtistSlug}`} target="_blank">
                    <code>{request.artist_f_id}</code>
                  </Link>
                ) : (
                  <code>{request.artist_f_id}</code>
                )}
              </dd>
            </div>
          )}
        </dl>

        {wasStopped(request) && (
          <p className={detail.notice}>
            This request was stopped. The catalogue changes it had already saved were kept — only the cover art
            and song videos it had left to add were skipped.
          </p>
        )}

        {request.result && (
          <CompletionSummary
            result={request.result}
            items={items}
            writes={data.writes || []}
            artistName={artistName || undefined}
            status={request.status}
          />
        )}

        {request.result?.trending && <TrendingSummary trending={request.result.trending} />}

        {query.isError && (
          <p className={detail.notice}>
            {query.error?.message || 'We lost contact with the server.'}{' '}
            <button type="button" className={classes.linkButton} onClick={() => query.refetch()}>
              Try again
            </button>
          </p>
        )}

        {data.runtime && data.runtime.available === false && (
          <p className={detail.notice}>{data.runtime.message}</p>
        )}

        {request.last_error && request.status !== 'needs_attention' && (
          <p className={detail.notice}>
            {request.last_error.message}
            {request.last_error.code && <span className={detail.code}>{request.last_error.code}</span>}
          </p>
        )}

        {data.current_generation === false && (
          <p className={detail.notice}>
            This request belongs to an older catalogue generation, so it can no longer be continued. Start a
            new one to pick up where it left off.
          </p>
        )}


        {request.status === 'needs_review' && reviews.length === 0 && (
          <section className={detail.section}>
            <h2 className={detail.sectionTitle}>Waiting on matching</h2>
            <p className={detail.sectionNote}>
              This request is ready for decisions but has not produced them yet. Check back shortly.
            </p>
          </section>
        )}

        {unanswerableArtistReviews.length > 0 && (
          <section className={detail.section}>
            <h2 className={detail.sectionTitle}>
              {unanswerableArtistReviews.length === 1
                ? 'An artist needs an engineer'
                : `${unanswerableArtistReviews.length} artists need an engineer`}
            </h2>
            <p className={detail.sectionNote}>
              {/* id_conflict and no_name (API-PAYLOAD.md) are the two shapes the
                  server itself cannot resolve from either a match or a create -
                  it 409s both - so unlike every other artist review, these never
                  get a decision card below. */}
              The server cannot resolve these on its own: a match or a create would both be rejected. Send an
              engineer the link to this page.
            </p>
            <ul className={detail.plainList}>
              {unanswerableArtistReviews.map(item => (
                <li key={item.id}>
                  {stagedName(item) || (item.review.incoming && item.review.incoming.name) || 'Unnamed artist'}
                  <span className={detail.summaryWhere}> — {item.review.reason}</span>
                  {(item.review.candidates || []).length > 0 && (
                    <span className={detail.summaryWhere}>
                      {' '}
                      (candidates: {item.review.candidates.map(c => c.f_id).filter(Boolean).join(', ')})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {request.status === 'needs_review' && reviews.length > 0 && (
          <section className={detail.section}>
            <h2 className={detail.sectionTitle} aria-live="polite">
              {openReviews.length > 0
                ? `${openReviews.length} ${openReviews.length === 1 ? 'decision' : 'decisions'} needed`
                : 'Your decisions are saved'}
            </h2>
            <p className={detail.sectionNote}>
              {openReviews.length > 0
                ? 'We could not tell whether these YouTube tracks are already in the catalogue. The sync is paused until you answer.'
                : unanswerableArtistReviews.length > 0
                  ? 'Nothing else here is waiting on you — but see the artists above that need an engineer.'
                  : 'The sync will carry on with them on its own. Nothing else is needed from you.'}
            </p>
            {openReviews.map(item => (
              <ReviewCard
                key={item.id}
                item={item}
                choice={effectiveChoice(item)}
                choices={choices}
                onChoose={(itemId, choice) => setChoices(prev => ({ ...prev, [itemId]: choice }))}
                onPickOne={onPickOne}
                disabled={actionsDisabled}
                writes={data.writes}
                artistSlugs={data.artist_slugs}
                artistYoutubeChannels={data.artist_youtube_channels}
                songAlbums={data.song_albums}
              />
            ))}
            {answered.length > 0 && (
              <ul className={detail.plainList}>
                {answered.map(item => (
                  <li key={item.id}>
                    <strong>{stagedName(item) || 'Untitled'}</strong>
                    <span className={detail.summaryWhere}> — {decisionSummary(item)}</span>
                  </li>
                ))}
              </ul>
            )}
            {openReviews.length > 0 && (
              <div className={detail.actions}>
                {/* Item 6: every open review needs its own decision before
                    Save is offered at all - no partial "SEND N OF M" send. */}
                <ButtonOutline
                  color="green"
                  size="small"
                  disable={actionsDisabled || decided !== openReviews.length}
                  text="SEND DECISIONS"
                  onClick={() => setConfirming('review')}
                />
                {decided < openReviews.length && (
                  <span className={detail.decisionsLeft}>
                    {openReviews.length - decided} decision{openReviews.length - decided === 1 ? '' : 's'} left
                  </span>
                )}
              </div>
            )}
          </section>
        )}


        {request.status === 'needs_attention' && (
          <section className={detail.section}>
            <h2 className={detail.sectionTitle}>Stopped</h2>
            <p className={detail.sectionNote}>
              {(request.last_error && request.last_error.message) || 'Something went wrong part way through.'}
              {request.last_error && request.last_error.code && (
                <span className={detail.code}> ({request.last_error.code})</span>
              )}
            </p>
          </section>
        )}

        {/* The grey status area: a durable headline (ACTIVITY_NAMES, from
            whichever Temporal activity is live) plus request.step's own live
            progress text underneath (e.g. "Pairing credited artist 9 of 9").
            Both read from lastStatusRef, not straight off data.runtime -
            see that ref's own comment on why: the raw activities list is
            legitimately empty between two activities, and gating this area
            on it made it blink. Shown for the whole non-terminal life of the
            request, never unmounted by a momentary gap. */}
        {!isSettled(request.status) && (lastStatusRef.current.headline || lastStatusRef.current.step) && (
          <div className={detail.activities}>
            <p className={detail.activityLine}>
              {lastStatusRef.current.headline && (
                <span className={detail.activityName}>{lastStatusRef.current.headline}</span>
              )}
              {lastStatusRef.current.step && (
                <span className={detail.activityStep}>{lastStatusRef.current.step}</span>
              )}
              {lastStatusRef.current.attempt > 1 && (
                <span className={detail.activityWhy}>
                  This is attempt {lastStatusRef.current.attempt}. The earlier ones did not succeed, and
                  it keeps retrying on its own.
                  {lastStatusRef.current.nextRetry && ` Next try around ${when(lastStatusRef.current.nextRetry)}.`}
                </span>
              )}
            </p>
          </div>
        )}

        <details className={detail.section}>
          <summary className={detail.disclosureSummary}>
            Releases and songs ({catalogueItems.length})
          </summary>
          <div className={detail.disclosureBody}>
            {catalogueItems.length === 0 && (
              <p className={detail.sectionNote}>Nothing has been pulled from YouTube yet.</p>
            )}
            {catalogueItems.length > 0 && (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.head}>Name</th>
                    <th className={classes.head}>Identity</th>
                    <th className={classes.head}>In our catalogue</th>
                    <th className={classes.head}>Source fetched</th>
                    <th className={classes.head}>How it was matched</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogueItems.map(item => (
                    <tr key={item.id} className={classes.row}>
                      <td className={classes.cell}>
                        {youtubeHref(item) ? (
                          <a
                            className={detail.itemName}
                            href={youtubeHref(item)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {stagedName(item)}
                          </a>
                        ) : (
                          <span className={classes.subject}>{stagedName(item) || 'Untitled'}</span>
                        )}
                        <span className={classes.meta}>
                          {[
                            item.item_type,
                            item.staged_data?.track && `track ${item.staged_data.track}`,
                            seconds(item.staged_data?.duration_seconds),
                            stagedExplicit(item) && 'explicit',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </td>
                      <td className={classes.cell}>
                        {item.source_id ? (
                          <code className={detail.code}>{item.source_id}</code>
                        ) : (
                          <span className={classes.meta}>—</span>
                        )}
                      </td>
                      <td className={classes.cell}>
                        {item.f_id && catalogueHref(item, data.artist_slugs, data.writes) ? (
                          <Link className={classes.subjectId} to={catalogueHref(item, data.artist_slugs, data.writes)}>
                            {item.f_id}
                          </Link>
                        ) : item.f_id ? (
                          <span className={classes.subjectId}>{item.f_id}</span>
                        ) : (
                          <span className={classes.meta}>Not in the catalogue yet</span>
                        )}
                        {item.reused_from_request_id && (
                          <span className={classes.meta}>reused from an earlier fetch</span>
                        )}
                      </td>
                      <td className={classes.cell}>{when(item.fetched_at)}</td>
                      <td className={classes.cell}>
                        {catalogueMatchLabel(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </details>

        {(data.writes || []).length > 0 && (
          <details className={detail.section}>
            <summary className={detail.disclosureSummary}>Catalogue changes ({data.writes.length})</summary>
            <div className={detail.disclosureBody}>
              {data.writes.map(write => (
                <WriteRow key={write.id} write={write} />
              ))}
            </div>
          </details>
        )}

        <WarningsSection groups={warningGroups} items={items} writes={data.writes} artistSlugs={data.artist_slugs} />

        {(data.events || []).length > 0 && (
          <details className={detail.section}>
            <summary className={detail.disclosureSummary}>History ({historyEntries.length})</summary>
            <div className={detail.disclosureBody}>
              <ol className={detail.history}>
                {historyEntries.map(entry => (
                  <li key={entry.id}>
                    <span className={detail.historyMessage}>{entry.message}</span>
                    <span className={detail.historyMeta}>{when(entry.at)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </details>
        )}

        <ConfirmDialog
          open={confirming === 'cancel'}
          title="Cancel this request?"
          dismissText="Keep going"
          body={
            request.result ? (
              <>
                <p>
                  The releases and songs are already saved — stopping now will not undo them. It only skips the
                  cover art and song videos it has left to add.
                </p>
                <p>This request cannot be restarted. To add the rest of the artwork and videos, start a new one.</p>
              </>
            ) : (
              <>
                <p>
                  Nothing has been saved to the catalogue yet — this sync saves everything at the end or not at
                  all, so stopping now leaves the catalogue exactly as it is.
                </p>
                <p>
                  What it has already fetched from YouTube stays saved for 24 hours, so a new request will not
                  have to fetch it again. This request cannot be restarted.
                </p>
              </>
            )
          }
          confirmText="Cancel request"
          busy={busy}
          onConfirm={() => {
            setConfirming(null)
            act('cancel')
          }}
          onCancel={() => setConfirming(null)}
        />

        <ConfirmDialog
          open={confirming === 'review'}
          title="Send these decisions?"
          body={
            <>
              <p>
                {[
                  merges && `${merges} will be merged into an existing entry`,
                  creates && `${creates} will be created as new`,
                  videoDecisions && `${videoDecisions} song video choice${videoDecisions === 1 ? '' : 's'} will be saved`,
                ]
                  .filter(Boolean)
                  .join(', ')}
                .
              </p>
              <p>This changes the catalogue and cannot be undone here.</p>
            </>
          }
          confirmText="Send decisions"
          busy={busy}
          onConfirm={() => {
            setConfirming(null)
            sendDecisions()
          }}
          onCancel={() => setConfirming(null)}
        />

        <div className={detail.footer}>
          {data.runtime && data.runtime.available && !data.runtime.workers && !isSettled(request.status) && (
            <p className={detail.footerNote}>
              No worker available right now. Processing continues automatically once one is.
            </p>
          )}

          {request.cancel_requested && !request.date_finished && (
            <p className={detail.footerNote}>Cancellation saved. Waiting for the run to stop.</p>
          )}

          {undelivered && !request.cancel_requested && (
            <p className={detail.footerNote}>Your action is saved and waiting to be picked up.</p>
          )}

          {/* Only worth saying when the page is NOT updating and you would
              otherwise assume it was. A live page needs no announcement. */}
          {!isSettled(request.status) && ((needsAPerson(request.status) && !undelivered) || query.isError) && (
            <p className={detail.footerNote}>
              This page is not updating on its own right now.{' '}
              <button type="button" className={classes.linkButton} onClick={() => query.refetch()}>
                Check for updates
              </button>
            </p>
          )}

          <div className={detail.techDetails}>
            <h2 className={detail.techTitle}>Additional technical details</h2>
            <dl className={detail.facts}>
              <div><dt>Workflow</dt><dd><code>{request.workflow_id || '—'}</code></dd></div>
              <div>
                <dt>Run</dt>
                <dd><code>{request.workflow_run_id || 'Not yet acknowledged'}</code></dd>
              </div>
            </dl>
          </div>
        </div>

      </div>
    </div>
  )
}
